import type { en } from './en';

/** Same keys as en, every value a string (checked at compile time). */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

/** 繁體中文介面字串。用語：專案、客戶、資產、漏洞、修補、緩解措施、工作區。 */
export const zhTW: Shape<typeof en> = {
  common: {
    showPassword: '顯示密碼',
    hidePassword: '隱藏密碼',
    loading: '載入中…',
    close: '關閉',
    cancel: '取消',
    save: '儲存',
    saved: '已儲存',
    create: '建立',
    edit: '編輯',
    rename: '重新命名',
    remove: '移除',
    archive: '封存',
    unarchive: '取消封存',
    archived: '已封存',
    actions: '操作',
    optional: '選填',
    copy: '複製',
    copied: '已複製',
    copyFailed: '複製失敗',
    loadMore: '載入更多',
    backHome: '回到總覽',
  },
  nav: {
    primary: '主要導覽',
    skipToContent: '跳到主要內容',
    openMenu: '開啟選單',
    closeMenu: '關閉選單',
    dashboard: '總覽',
    findings: '漏洞發現',
    clients: '客戶與專案',
    eol: '停止支援通知',
    agents: '程式碼代理',
    workspaceSection: '工作區',
    members: '成員',
    settings: '設定與 SLA',
    notifications: '通知',
    apiTokens: 'API 權杖',
    audit: '稽核記錄',
    reports: '報告',
    github: 'GitHub',
    billing: '帳務',
    plans: '方案',
    staffConsole: '管理後台',
    workspace: '工作區',
    chooseWorkspace: '選擇工作區',
    createWorkspace: '＋ 新增工作區',
    noWorkspace: '建立或選擇一個工作區以開始。',
    language: '語言',
    userMenu: '帳號選單',
    help: '說明',
    dataSources: '資料來源與授權',
    helpTitle: '說明（在新分頁開啟使用手冊）',
    feedback: '意見回饋',
    feedbackTitle: '向 Smoke 提出問題、建議或回饋',
    account: '帳號設定',
    signOut: '登出',
  },
  legal: {
    terms: '服務條款',
    privacy: '隱私權政策',
    refund: '退款政策',
  },
  theme: {
    toggle: '主題：{{mode}}（點選切換）',
    light: '淺色',
    dark: '深色',
    system: '跟隨系統',
  },
  disclaimer: '修補內容僅為建議。部署前請檢查變更並執行測試。',
  validation: {
    required: '必填',
    email: '請輸入有效的電子郵件地址',
    passwordMin: '至少需要 12 個字元',
    passwordMax: '最多 256 個字元',
    passwordMismatch: '兩次輸入的密碼不一致',
  },
  errors: {
    title: '發生錯誤',
    unexpected: '發生未預期的錯誤。請再試一次，或回到總覽。',
    notFoundTitle: '找不到頁面',
    notFoundBody: '此頁面不存在，或你沒有存取權限。',
    NETWORK: '無法連線到 Smoke。請檢查網路連線後再試一次。',
    UNKNOWN: '發生錯誤（{{code}}）。請再試一次。',
    VALIDATION_FAILED: '部分欄位內容無效，請檢查後再試一次。',
    UNAUTHENTICATED: '登入階段已結束，請重新登入。',
    CSRF_TOKEN_INVALID: '安全權杖已過期，請重新載入頁面後再試一次。',
    FORBIDDEN: '你沒有執行此操作的權限。',
    NOT_FOUND: '找不到資料。',
    CONFLICT: '與目前狀態衝突，請重新載入後再試一次。',
    RATE_LIMITED: '嘗試次數過多，請稍候再試。',
    PAYLOAD_TOO_LARGE: '送出的內容太大。',
    UNSUPPORTED_MEDIA_TYPE: '這裡不接受這種格式的內容。',
    NOT_IMPLEMENTED: '此功能尚未開放。',
    INTERNAL_ERROR: 'Smoke 發生問題，請稍後再試。',
    AUTH_INVALID_CREDENTIALS: '電子郵件或密碼不正確。',
    AUTH_EMAIL_NOT_VERIFIED: '請先驗證你的電子郵件地址。',
    AUTH_TOKEN_INVALID: '此連結無效或已過期。',
    AUTH_MFA_PENDING: '請輸入雙重驗證碼以繼續。',
    AUTH_MFA_INVALID_CODE: '驗證碼不正確。',
    AUTH_MFA_ALREADY_ENABLED: '雙重驗證已經啟用。',
    AUTH_MFA_NOT_ENABLED: '尚未啟用雙重驗證。',
    AUTH_MFA_ENROLLMENT_REQUIRED: '此工作區要求雙重驗證。請在帳號設定中啟用。',
    AUTH_OAUTH_PROVIDER_DISABLED: '此登入服務目前無法使用。',
    AUTH_OAUTH_FAILED: '透過登入服務登入失敗，請再試一次。',
    AUTH_OAUTH_EMAIL_UNVERIFIED: '登入服務未確認你的電子郵件地址。',
    PASSWORD_NOT_SET:
      '你的帳號使用 Google 或 GitHub 登入，沒有設定密碼。請使用「忘記密碼？」設定密碼。',
    REAUTH_REQUIRED: '為了安全，請先登出再重新登入，然後再試一次。',
    ACCOUNT_CONFIRM_MISMATCH: '輸入的電子郵件地址與你的帳號不符。',
    ACCOUNT_SOLE_OWNER:
      '你是某個仍有其他成員的工作區唯一的擁有者。請先把另一位成員設為擁有者，或先刪除該工作區。',
    ACCOUNT_BILLING_OWNER:
      '你是某個仍有其他成員的工作區的帳務擁有者。請先在工作區設定把帳務交給另一位擁有者，或先刪除該工作區。',
    STAFF_ACCOUNT: 'Smoke 員工帳號須由員工管理員移除。',
    WORKSPACE_NOT_FOUND: '找不到工作區，或你不是其成員。',
    WORKSPACE_ROLE_INSUFFICIENT: '你在此工作區的角色無法執行此操作。',
    WORKSPACE_DELETION_PENDING: '此工作區已排定刪除。',
    MEMBER_NOT_FOUND: '找不到成員。',
    MEMBER_LAST_OWNER: '工作區至少需要一位擁有者。',
    MEMBER_BILLING_OWNER: '這位成員是帳務擁有者。請先在工作區設定把帳務交給另一位擁有者。',
    BILLING_OWNER_NOT_OWNER: '帳務只能交給這個工作區的擁有者。請先把對方設為擁有者。',
    BILLING_OWNER_ONLY: '只有帳務擁有者可以交出帳務。',
    BILLING_OWNER_MISSING: '這個工作區沒有帳務擁有者，請聯絡我們。',
    INVITATION_INVALID: '此邀請無效。',
    INVITATION_EXPIRED: '此邀請已過期，請要求重新寄送。',
    INVITATION_EMAIL_MISMATCH: '此邀請是寄給另一個電子郵件地址。',
    INVITATION_ALREADY_MEMBER: '你已經是此工作區的成員。',
    CLIENT_NOT_FOUND: '找不到客戶。',
    CLIENT_NOT_EMPTY: '請先移動或刪除此客戶的專案。',
    PROJECT_NOT_FOUND: '找不到專案。',
    PROJECT_ARCHIVED: '此專案已封存。',
    ASSET_NOT_FOUND: '找不到資產。',
    ASSET_DATABASE_ENGINE_REQUIRED: '資料庫資產要有一個資料庫引擎和版本。',
    ASSET_ADDRESSES_SERVER_ONLY: '只有伺服器可以設定 IP 位址。',
    ASSET_BASE_IMAGE_IMAGE_ONLY: '只有容器映像檔可以設定基底映像檔。',
    DEPLOYMENT_INVALID: '只有其他資產可以部署到伺服器，而且要在同一個專案。',
    INVENTORY_NOT_APPLICABLE: '資料庫資產沒有套件清單可以上傳。',
    ADVISORY_NOT_FOUND: '這個工作區沒有這則漏洞公告。',
    ASSET_KIND_NOT_SUPPORTED: '尚不支援此資產類型。',
    ASSET_ARCHIVED: '此資產已封存。',
    API_TOKEN_NOT_FOUND: '找不到權杖。',
    API_TOKEN_SCOPE_INVALID: '這些權限範圍不適用於此權杖類型。',
    API_TOKEN_NOT_RENAMABLE: '已撤銷的權杖和 MCP 用戶端授權不能改名。',
    INVENTORY_NO_FILES: '請至少加入一個檔案。',
    INVENTORY_FILE_TOO_LARGE: '有檔案超過 20 MB。',
    INVENTORY_TOO_MANY_FILES: '單次上傳的檔案過多。',
    INVENTORY_UPLOAD_TOO_LARGE: '單次上傳的總大小超過 100 MB，請分批上傳。',
    INVENTORY_PARSE_FAILED: '有檔案無法解析，因此未儲存任何內容。',
    INVENTORY_LOCKFILE_REQUIRED:
      'package.json 只有版本範圍，沒有實際安裝的版本，無法準確比對漏洞。請一併上傳 lockfile：package-lock.json（npm install --package-lock-only）、pnpm-lock.yaml（pnpm install --lockfile-only）或 yarn.lock（yarn install）。',
    SNAPSHOT_NOT_FOUND: '找不到快照。',
    FINDING_NOT_FOUND: '找不到漏洞發現。',
    FINDING_TRANSITION_NOT_ALLOWED: '無法從目前的狀態變更為此狀態。',
    CURSOR_INVALID: '清單已變動，請重新載入。',
    MITIGATION_NOT_FOUND: '選取的緩解措施已不再提供，請重新載入頁面。',
    FIX_PROMPT_TOO_MANY_FINDINGS: '合併提示最多包含 10 筆漏洞發現。',
    FIX_PROMPT_MIXED_ASSETS: '合併提示只能包含同一個資產的漏洞發現。',
    FIX_PROMPT_NOT_AVAILABLE: '此漏洞發現沒有可用的修復提示。',
    AI_REMEDIATION_UNAVAILABLE: '此漏洞發現目前無法產生 AI 分析。',
    EOL_NOTICE_NOT_FOUND: '找不到這筆停止支援通知。',
    EOL_PROMPT_NOT_AVAILABLE: '這筆通知沒有可用的升級提示詞。',
    INSUFFICIENT_SCOPE: '這個權杖沒有執行此動作的權限範圍。',
    MITIGATION_NOT_AGENT_APPLICABLE: '程式碼代理無法套用這項緩解措施。',
    NOTIFICATION_CHANNEL_NOT_CONFIGURED: '這台 Smoke 伺服器尚未設定這個聊天管道。',
    NOTIFICATION_CHANNEL_NOT_FOUND: '這個聊天連結已不存在，請重新載入頁面。',
    NOTIFICATION_ROUTE_INVALID: '有轉送規則指向已不存在的客戶或群組。',
    UNSUBSCRIBE_TOKEN_INVALID: '這個取消訂閱連結無效或已過期。',
    WEBHOOK_SIGNATURE_INVALID: '請求的簽章無效。',
    REMEDIATION_NOT_RATEABLE: '無法評分此修復建議。',
    DOWNLOAD_LINK_INVALID: '下載連結已過期，請重新開啟列表取得新連結。',
    PLAN_LIMIT_REACHED: '已達方案上限，請升級方案以新增更多。',
    PLAN_FEATURE_UNAVAILABLE: '目前方案不含此功能，升級後即可使用。',
    PLAN_NOT_FOUND: '無法使用此方案。',
    BILLING_NOT_CONFIGURED: '此 Smoke 伺服器尚未設定線上付款。',
    BILLING_DETAILS_REQUIRED: '請先填寫帳務資料。',
    BILLING_ALREADY_SUBSCRIBED: '此工作區已有付費方案，請改用升級。',
    BILLING_NOT_SUBSCRIBED: '請先選擇付費方案。',
    BILLING_NOT_AN_UPGRADE: '此方案不是目前方案的升級。',
    BILLING_NOT_A_DOWNGRADE: '此方案不是目前方案的降級。',
    BILLING_OPERATION_PENDING: '此工作區有另一筆付款仍在確認中，確認完成後再試一次。',
    BILLING_UNAVAILABLE: '付款服務目前無法使用，沒有任何扣款，請稍後再試。',
    BILLING_PAYMENT_FAILED: '信用卡遭拒，請改用其他信用卡再試一次。',
    BILLING_WEBHOOK_DISABLED: '模擬模式下不接受付款通知。',
    BILLING_WEBHOOK_SIGNATURE_INVALID: '付款通知的簽章無效。',
    PAYMENT_NOT_FOUND: '找不到付款。',
    REFUND_PENDING: '此付款的退款仍在處理中。',
    REFUND_NOT_ALLOWED: '此付款無法在這裡退款（僅限信用卡付款，且不超過付款金額）。',
    STAFF_ONLY: '僅限 Smoke 工作人員使用。',
    STAFF_ROLE_INSUFFICIENT: '此操作需要管理員工作人員角色。',
    STAFF_ACCESS_REQUIRED: '請先填寫原因，開啟此工作區的唯讀存取。',
    STAFF_READ_ONLY: '工作人員存取為唯讀，沒有進行任何變更。',
    USER_NOT_FOUND: '沒有使用此 Email 的使用者，對方需要先註冊。',
    FEATURE_FLAG_NOT_FOUND: '找不到功能旗標。',
    FALSE_POSITIVE_NOT_FOUND: '此回報已不存在。',
    STAFF_ALERT_NOT_FOUND: '此警示已不存在。',
    FEEDBACK_NOT_FOUND: '這則意見回饋已不存在。',
    FEEDBACK_OPEN_LIMIT: '你已有 20 則意見回饋等待回覆，請等我們回覆部分之後再送出。',
    REPORT_NOT_FOUND: '找不到報告。',
    REPORT_NOT_READY: '檔案尚未完成。',
    REPORT_PERIOD_INVALID: '期間必須是 1 到 366 天的有效日期。',
    GITHUB_NOT_CONFIGURED: '此 Smoke 伺服器尚未設定 GitHub。',
    GITHUB_STATE_INVALID: 'GitHub 設定連結已過期，請重新開始。',
    GITHUB_INSTALLATION_NOT_FOUND: '此 GitHub 安裝已不再連接。',
    GITHUB_REPOSITORY_NOT_FOUND: 'Smoke GitHub App 無法存取此儲存庫。',
    GITHUB_REPOSITORY_ALREADY_TRACKED: '此工作區已在追蹤這個儲存庫。',
    GITHUB_BRANCH_NOT_FOUND: '儲存庫中沒有這個分支。',
    GITHUB_MANIFEST_PATH_INVALID: '清單檔路徑無效。',
    GITHUB_API_ERROR: 'GitHub 回傳錯誤或無法連線，請稍後再試。',
  },
  auth: {
    termsNotice:
      '繼續使用即表示你同意<terms>服務條款</terms>，並已閱讀<privacy>隱私權政策</privacy>。',
    loginTitle: '登入 Smoke',
    loginSubtitle: '為接案團隊與小型團隊打造的漏洞追蹤服務。',
    signUpTitle: '建立 Smoke 帳號',
    signUpSubtitle: '幾分鐘內就能看到第一份漏洞報告。',
    email: '電子郵件',
    password: '密碼',
    passwordHint: '至少 12 個字元，使用一句好記的通關密語也很適合。',
    displayName: '名稱',
    signIn: '登入',
    signUp: '註冊',
    createAccount: '建立帳號',
    noAccount: '第一次使用 Smoke？',
    haveAccount: '已經有帳號了？',
    forgotPassword: '忘記密碼？',
    or: '或',
    continueWith: '使用 {{provider}} 繼續',
    oauthNotConfigured: '此伺服器尚未設定 Google 與 GitHub 登入。',
    checkEmailTitle: '請查看你的電子郵件',
    checkEmailBody: '如果 {{email}} 可以用於 Smoke，我們已寄出驗證連結，連結 24 小時內有效。',
    checkEmailGeneric: '請開啟我們寄給你的驗證連結。需要新的連結嗎？請輸入你的電子郵件地址。',
    resendVerification: '重新寄送驗證連結',
    verificationResent: '如果此地址需要驗證，新的連結已經寄出。',
    backToSignIn: '回到登入',
    verifyTitle: '驗證電子郵件',
    verifying: '正在驗證你的電子郵件…',
    verified: '你的電子郵件地址已驗證，現在可以登入了。',
    twoFactorTitle: '雙重驗證',
    twoFactorSubtitle: '請輸入驗證器 App 顯示的 6 位數驗證碼。',
    recoverySubtitle: '請輸入一組復原碼，每組只能使用一次。',
    authCode: '驗證碼',
    recoveryCode: '復原碼',
    verify: '驗證',
    useRecoveryCode: '改用復原碼',
    useAuthenticator: '改用驗證器 App',
    cancel: '取消',
    forgotTitle: '重設密碼',
    forgotSubtitle: '我們會寄給你設定新密碼的連結。',
    sendResetLink: '寄送重設連結',
    forgotSent: '如果有帳號使用此地址，重設連結已經寄出。',
    resetTitle: '設定新密碼',
    newPassword: '新密碼',
    confirmPassword: '再次輸入新密碼',
    setPassword: '設定密碼',
    resetDone: '密碼已變更，請使用新密碼登入。',
  },
  feedback: {
    title: '意見回饋',
    description:
      '有問題、建議或想法都可以告訴我們。Smoke 團隊會閱讀每一則訊息並在這裡回覆，同時寄 Email 通知你。',
    newTitle: '送出意見',
    kindLabel: '類型',
    kind: {
      question: '問題',
      suggestion: '建議',
      feedback: '回饋',
    },
    kindHint: {
      question: '有功能不正常，或不確定怎麼操作。',
      suggestion: '希望新增或改進的功能。',
      feedback: '其他想告訴我們的事。',
    },
    titleLabel: '主旨',
    titlePlaceholder: '簡短描述',
    bodyLabel: '內容',
    bodyHint:
      '回報問題時，請說明你做了什麼、看到什麼。我們也會收到你送出前所在的頁面、Smoke 版本和瀏覽器資訊。',
    sensitiveWarning: '請勿填寫個人資料、密碼、API 權杖等敏感資訊。Smoke 團隊不會向你索取密碼。',
    send: '送出',
    sent: '已送出，謝謝你！',
    mineTitle: '你送出的意見',
    empty: '你還沒有送出任何意見。',
    replyFrom: 'Smoke 的回覆',
    status: {
      new: '已收到',
      planned: '已排入計畫',
      in_progress: '處理中',
      done: '已完成',
      declined: '不採納',
    },
  },
  account: {
    title: '帳號設定',
    profile: '個人資料',
    language: '語言',
    languageHint: '用於介面、電子郵件、修復建議與修復提示。',
    loginMethods: '登入方式',
    twoFactor: '雙重驗證',
    twoFactorBody: '使用驗證器 App 產生的驗證碼（TOTP）保護你的帳號。',
    enabled: '已啟用',
    disabled: '未啟用',
    setUp2fa: '設定雙重驗證',
    scanQr: '用驗證器 App 掃描此 QR Code，或手動輸入金鑰。',
    qrAlt: '驗證器 App 使用的 QR Code',
    manualKey: '金鑰',
    enterCode: '輸入 App 顯示的 6 位數驗證碼。',
    enable2fa: '啟用',
    disable2fa: '停用',
    regenerateCodes: '產生新的復原碼',
    recoveryCodesTitle: '請立即保存你的復原碼',
    recoveryCodesBody: '復原碼只會顯示這一次。遺失裝置時，每組復原碼可登入一次。',
    downloadCodes: '下載文字檔',
    password: '密碼',
    passwordBody: '變更密碼後，其他所有裝置都會登出。',
    currentPassword: '目前的密碼',
    changePassword: '變更密碼',
    passwordChanged: '密碼已變更，其他裝置已登出。',
    passwordMismatch: '兩次輸入的新密碼不一致。',
    noPassword: '你使用 Google 或 GitHub 登入，這個帳號沒有密碼。可以透過「忘記密碼？」設定密碼。',
    setPasswordLink: '設定密碼',
    dangerZone: '危險區域',
    deleteAccount: '刪除帳號',
    deleteAccountBody:
      '刪除你的帳號並在所有裝置登出。登入方式、雙重驗證設定、個人 API 權杖和 MCP 連線都會移除；工作區稽核紀錄中的操作紀錄會保留。',
    deleteTokensKept:
      '只會刪除你的帳號。你建立的專案和資產 API 權杖會保留在原工作區並繼續有效，使用它們的 CI 不會中斷。工作區管理員可以在「API 權杖」頁面撤銷。',
    deleteAccountTitle: '確定要刪除帳號嗎？',
    deleteBlocking:
      '你是下列工作區唯一的擁有者，而且工作區還有其他成員。刪除帳號前，請先把另一位成員設為擁有者，或刪除該工作區：',
    deleteBillingOwner:
      '你是下列工作區的帳務擁有者，而且工作區還有其他成員。刪除帳號前，請先在工作區設定把帳務交給另一位擁有者，或刪除該工作區：',
    deleteWithAccount: '下列工作區只有你一位成員，會隨帳號一起刪除，並在 30 天內清除資料：',
    deleteLeaving: '你會離開下列工作區：',
    staffCannotDelete: 'Smoke 員工帳號無法在這裡刪除，請洽員工管理員。',
    recentSignIn:
      '你的帳號沒有密碼，必須在 15 分鐘內登入過才能刪除。如果已超過，請先登出再重新登入。',
    confirmEmail: '輸入你的電子郵件地址以確認',
    deleteForever: '刪除我的帳號',
  },
  workspace: {
    welcomeTitle: '歡迎使用 Smoke',
    createTitle: '新增工作區',
    createBody: '工作區包含你的客戶、專案與漏洞發現。接下來可以邀請團隊成員。',
    name: '工作區名稱',
    nameHint: '通常是你的公司或團隊名稱。',
    timeZone: '時區',
    timeZoneHint: '日期與 SLA 到期日會以此時區顯示。',
    create: '建立工作區',
  },
  invitation: {
    title: '工作區邀請',
    accepting: '正在接受邀請…',
    signedInAs: '你目前以 {{email}} 登入。',
  },
  members: {
    title: '成員',
    description: '可以查看並處理此工作區的人員。',
    listTitle: '成員',
    member: '成員',
    role: '角色',
    roleFor: '{{name}} 的角色',
    joined: '加入時間',
    you: '你',
    billingOwner: '帳務擁有者',
    remove: '移除',
    leave: '離開',
    confirmRemove:
      '要將 {{name}} 從此工作區移除嗎？此成員的個人 API 權杖會失效；此成員建立的專案和資產 API 權杖會保留並繼續有效。',
    confirmLeave:
      '要離開此工作區嗎？你的個人 API 權杖會失效；你建立的專案和資產 API 權杖會保留並繼續有效。',
    inviteTitle: '邀請成員',
    inviteBody: '邀請在 7 天後失效。',
    sendInvite: '寄送邀請',
    invited: '已寄送邀請給 {{email}}',
    pendingTitle: '待接受的邀請',
    noInvitations: '沒有待接受的邀請',
    expires: '到期時間',
    expired: '已過期',
    revoke: '撤銷',
    rolesTitle: '角色說明',
    roleHelp: {
      owner: '所有權限，包含帳務與刪除工作區。',
      admin: '管理成員、通知管道與所有專案。',
      member: '只能存取指派給自己的客戶與專案。',
    },
  },
  clients: {
    title: '客戶與專案',
    description: '客戶底下有專案；每個專案有一或多個資產，各自有套件清單。',
    new: '新增客戶',
    newBody: '例如你負責維護網站的公司。',
    name: '客戶名稱',
    renameTitle: '重新命名客戶',
    showArchived: '顯示已封存',
    noClient: '未指定客戶',
    projectCount_one: '{{count}} 個專案',
    projectCount_other: '{{count}} 個專案',
    emptyTitle: '還沒有客戶',
    emptyBody: '建立客戶、新增專案並上傳 lockfile，就能看到其中的漏洞。',
  },
  projects: {
    new: '新增專案',
    name: '專案名稱',
    client: '客戶',
    environment: '環境',
    description: '專案說明',
    descriptionHint: '選填。這個專案是做什麼的，方便團隊辨識。',
    environmentHint: '測試（staging）與開發環境的專案，優先順序會降低一級。',
    editTitle: '編輯專案',
    openFindings: '未處理的漏洞',
    noOpen: '沒有未處理項目',
    viewOpenFindings_one: '查看 {{count}} 筆未處理漏洞',
    viewOpenFindings_other: '查看 {{count}} 筆未處理漏洞',
    viewFindings: '查看漏洞發現',
    emptyTitle: '還沒有專案',
    emptyBody: '為此客戶新增一個專案。',
    archivedNotice: '此專案已封存：不會掃描，也不會發送通知。',
  },
  stack: {
    title: '技術堆疊',
    hint: '只用於偵測停止支援的軟體，以及作為修復提示的背景資訊，不會用於漏洞比對。',
    assetBody: '從這個資產的清單自動偵測，也包含手動補充的項目。',
    onThisServer: '這台伺服器上的資產',
    fromServers: '所在伺服器提供',
    archivedBody: '已封存的資產不列入技術堆疊。',
    manualTitle: '手動補充技術堆疊',
    manualBody: '自動偵測不到的才需要填，例如用 nvm 安裝的 Node.js、自行編譯的 PostgreSQL。',
    generatedBody:
      '由這個專案的資產自動產生：主機清單提供作業系統和已安裝的服務，lockfile 提供框架。偵測不到的項目到資產頁手動補充。',
    emptyTitle: '還沒有技術堆疊',
    emptyBody: '上傳主機清單或 lockfile 後會自動產生。',
    removeDeclared: '移除',
    database: '資料庫',
    service: '服務',
    addDatabase: '新增資料庫',
    addService: '新增服務',
    runtime: '執行環境',
    framework: '框架',
    os: '作業系統',
    version: '版本',
    release: '版本',
    invalidVersion: '只能使用數字與點，例如 20、8.3 或 22.04。',
    choose: '請選擇…',
    addRuntime: '新增執行環境',
    addFramework: '新增框架',
    addOs: '新增作業系統',
    itemName: '{{kind}} {{n}}',
    itemVersion: '{{kind}} {{n}} {{version}}',
    removeItem: '移除{{kind}} {{n}}',
    limit: '最多 {{count}} 項。',
    duplicate: '同一個項目和版本重複了。',
    notTracked: '沒有停止支援資料，不會收到停止支援提醒。',
    notTrackedLatest: '沒有停止支援資料，不會收到停止支援提醒。目前最新版 {{version}}。',
    group: {
      js: 'JavaScript / TypeScript',
      php: 'PHP',
      python: 'Python',
      other: '其他語言',
    },
  },
  catalogDelete: {
    confirm: '刪除',
    typeName: '輸入「{{name}}」以確認',
    reason: '原因（選填）',
    archiveInstead:
      '刪除後無法在 Smoke 中復原，資料會在 30 天後永久清除。如果只是要停止掃描和通知、保留紀錄，請改用封存。',
    client: '刪除客戶',
    clientTitle: '確定要刪除客戶「{{name}}」嗎？',
    clientBody:
      '只能刪除沒有專案的客戶：請先把專案移到其他客戶或刪除。此客戶的成員指派會一併移除。',
    project: '刪除專案',
    projectTitle: '確定要刪除專案「{{name}}」嗎？',
    projectBody:
      '專案和底下所有資產，連同套件清單和漏洞發現，都會從 Smoke 移除。此專案和其資產的 API 權杖會失效。',
    asset: '刪除資產',
    assetTitle: '確定要刪除資產「{{name}}」嗎？',
    assetBody: '資產連同套件清單和漏洞發現，都會從 Smoke 移除。此資產的 API 權杖會失效。',
    deleted: '已刪除。',
  },
  servers: {
    runsOn: '部署在：',
    new: '新增伺服器',
    newBody: '先建立伺服器，再把上面的 repo、Docker 和資料庫加進來。還沒掃描也可以先建。',
    addExisting: '加入現有資產',
    addNew: '新增到這台',
    unlink: '移除',
    place: '部署到伺服器',
    nothingOnServer: '這台伺服器上還沒有其他資產。',
    unplacedTitle: '沒有綁定伺服器',
    unplacedBody: '例如還沒部署的 repo、RDS 等託管服務。',
    addTitle: '把資產部署到伺服器',
    editTitle: '編輯連接埠和網域',
    reachNote:
      '連接埠和網域都是選填，只有這個專案的成員看得到，不會出現在通知、修復提示，也不會傳給 AI。',
    server: '伺服器',
    asset: '資產',
    ports: '連接埠',
    portsHint: '用逗號分隔，例如 443, 8080。',
    portsInvalid: '連接埠要是 1 到 65535 的數字。',
    domains: '網域',
    domainsHint: '用逗號分隔，例如 api.example.com。',
    addresses: 'IP 位址',
    addressesHint: '用逗號分隔，IPv4 或 IPv6 都可以。',
    addressesNote: '選填。只有這個專案的成員看得到，不會出現在通知、修復提示，也不會傳給 AI。',
    noAddresses: '尚未填寫。',
    engine: '資料庫引擎',
    engineVersion: '版本',
    databaseTitle: '資料庫',
    databaseNote:
      '用來追蹤停止支援日期，目前不比對漏洞。自架的資料庫如果是用 apt 或 apk 安裝，上傳主機清單就會自動偵測。',
  },
  assets: {
    baseImage: '基底映像檔',
    baseImageHint:
      '選填，Dockerfile 的 FROM，例如 node:20-alpine，讓 AI 的基底映像檔建議更準確。沒填時依清單的發行版判斷（例如 alpine:3.24）。只有公開映像檔會送出：Docker Hub 官方映像檔，以及 prom、nginxinc、bitnami、grafana 等知名發行者；自己的 registry 或組織不會送出，改用發行版判斷。',
    aiBaseImage: 'AI 分析使用的基底映像檔',
    aiBaseImageTyped: '你填寫的',
    aiBaseImageDerived: '依清單的發行版判斷',
    aiBaseImageNotSent: '你填的 {{typed}} 不是公開映像檔，不會送出，改用依發行版判斷的值。',
    baseImageInvalid: '請輸入映像檔名稱，例如 node:20-alpine。',
    noInventoryOs: '尚無套件清單。按「用指令收集」取得指令，執行後上傳產生的檔案。',
    kind: {
      manifest_upload: 'Lockfile 或 manifest',
      manifest_uploadHint: '應用程式：上傳它的 lockfile 或 SBOM。',
      host: 'Linux 伺服器',
      hostHint: '執行 Ubuntu、Debian 或 Alpine 的伺服器或 VM：執行一段指令，上傳它產生的檔案。',
      container_image: '容器映像檔',
      container_imageHint:
        '以 Ubuntu、Debian 或 Alpine 為基底的映像檔：在映像檔中執行指令，或上傳它的 SBOM。',
      database: '資料庫',
      databaseHint: '自架或託管的資料庫：填入引擎和版本，追蹤停止支援日期。目前不比對漏洞。',
    },
    repositoryUrl: '程式碼儲存庫網址',
    repositoryUrlHint: '選填。讓程式碼代理能透過 `git remote get-url origin` 找到此資產。',
    title: '資產',
    description: '每個資產有自己的套件清單：一組 lockfile 或一份 SBOM。',
    new: '新增資產',
    newBody: '例如專案中的一個應用程式或服務。',
    name: '資產名稱',
    nameHint: '例如「web」或「api」。',
    kindNote: '主機、容器映像檔和資料庫與其他資產一樣，計入方案的資產數。',
    type: '類型',
    lastInventory: '最近一次清單',
    never: '從未',
    emptyTitle: '還沒有資產',
    emptyBody: '從新增伺服器開始，再把上面的 repo、Docker 和資料庫加進來。',
    lastInventoryAt: '最近一次清單：{{date}}',
    noInventory: '尚無套件清單，請在下方上傳 lockfile。',
    editTitle: '編輯資產',
    archivedBody: '封存的資產不會掃描，也不會發出通知。取消封存後才能再上傳。',
    privateLookup: {
      askTitle_one: '有 {{count}} 個套件來自非公開的 registry',
      askTitle_other: '有 {{count}} 個套件來自非公開的 registry',
      body: '這些可能是你們自己的內部套件，或是透過公司 registry 代理下載的套件。為了建議升級哪個上層套件，Smoke 需要到公開 registry（npm、PyPI、Packagist）查詢相依路徑上的套件，套件名稱會因此送到這些服務。只會送出名稱，版本和你們的程式碼都不會送出。',
      untilDecided:
        '在你決定之前，這些名稱不會送出，所以經過它們的修補建議可能不完整。不論是否同意，AI 修復建議都不會收到這些名稱。',
      allow: '允許查詢',
      deny: '不要送出',
      adminOnly: '由這個工作區的管理員決定。',
      allowed_one: '{{count}} 個私有套件：允許到公開 registry 查詢名稱。',
      allowed_other: '{{count}} 個私有套件：允許到公開 registry 查詢名稱。',
      denied_one: '{{count}} 個私有套件：不會把名稱送到公開 registry。',
      denied_other: '{{count}} 個私有套件：不會把名稱送到公開 registry。',
    },
    archivedNotice: '此資產已封存。',
  },
  upload: {
    osDescription:
      '按「用指令收集」取得指令，執行後上傳產生的檔案或貼上內容（容器映像檔也可以上傳 CycloneDX SBOM）。Smoke 只保存套件名稱、版本，以及收集視窗列出的作業系統資訊。',
    osDropTitle: '將指令產生的檔案拖放到這裡',
    osDropHint: '每個檔案上限 20 MB。',
    hostFormats: 'smoke-host.txt',
    imageFormats: 'smoke-image.txt · CycloneDX／SPDX JSON',
    osPath: '檔案',
    title: '上傳套件清單',
    description:
      '此資產的 lockfile 或 SBOM。Smoke 只保存套件名稱、版本與路徑；檔案會在 24 小時內刪除。',
    dropTitle: '將 lockfile 或資料夾拖放到這裡',
    dropHint: 'monorepo 中的多個 manifest 會保留相對路徑。每個檔案上限 20 MB。',
    chooseFiles: '選擇檔案',
    chooseFolder: '選擇資料夾',
    formats:
      'package-lock.json · npm-shrinkwrap.json · pnpm-lock.yaml · yarn.lock · poetry.lock · uv.lock · Pipfile.lock · requirements.txt · composer.lock · CycloneDX／SPDX JSON',
    ignoredFromFolder_one: '資料夾中有 {{count}} 個檔案不是支援的格式，已略過。',
    ignoredFromFolder_other: '資料夾中有 {{count}} 個檔案不是支援的格式，已略過。',
    selected: '已選取的檔案',
    path: '在儲存庫中的路徑',
    pathFor: '{{name}} 的路徑',
    size: '大小',
    removeFile: '移除 {{path}}',
    tooLarge: '超過 20 MB。',
    unsupported: '不是支援的 manifest 檔名。',
    tooMany: '每次最多上傳 {{max}} 個檔案。',
    duplicatePaths: '有兩個檔案路徑相同，請修改路徑讓每個都不重複。',
    rejected: '上傳遭拒',
    wholeInventory: '每次上傳都會取代此資產的完整清單。只要有任何檔案失敗，就不會儲存任何內容。',
    clear: '清除',
    submit_one: '上傳 {{count}} 個檔案',
    submit_other: '上傳 {{count}} 個檔案',
  },
  report: {
    osLeftOut: {
      notInstalled_one: '{{count}} 個套件有列出但未安裝，已略過。',
      notInstalled_other: '{{count}} 個套件有列出但未安裝，已略過。',
      malformed_one: '{{count}} 行無法讀取，已略過。',
      malformed_other: '{{count}} 行無法讀取，已略過。',
      unknownSections_one: '已忽略 {{count}} 個無法辨識的段落。',
      unknownSections_other: '已忽略 {{count}} 個無法辨識的段落。',
    },
    title: '解析報告',
    files: '檔案',
    components: '套件',
    skipped: '已略過',
    unresolved: '無法解析',
    matching: '比對',
    viewFindings: '查看漏洞發現',
    snapshotLine: '快照 #{{sequence}}，上傳於 {{date}}',
    matchFailed: '比對失敗，Smoke 會自動重試。',
    customSourceTitle: '來自非預設 registry 的套件',
    customSourceBody:
      '這些套件來自公開預設以外的 registry。Smoke 仍會照常比對，但它們可能是與公開套件同名的私有套件，因此相關的漏洞發現可能是誤判。',
    componentCount_one: '{{count}} 個套件',
    componentCount_other: '{{count}} 個套件',
    skippedCount_one: '略過 {{count}} 個',
    skippedCount_other: '略過 {{count}} 個',
    unresolvedCount_one: '{{count}} 個無法解析',
    unresolvedCount_other: '{{count}} 個無法解析',
    entry: '項目',
    line: '行號',
    reason: '原因',
    clean: '此檔案的所有項目都已解析。',
  },
  snapshots: {
    title: '快照紀錄',
    description: '此資產最近 30 份套件清單。選擇兩份即可比較差異。',
    uploaded: '上傳時間',
    source: '來源',
    current: '目前',
    compareFrom: '比較起點',
    compareTo: '比較終點',
    compareFromN: '從快照 {{n}} 開始比較',
    compareToN: '比較到快照 {{n}}',
    viewReport: '查看報告',
    compareHint: '選擇「起點」與「終點」快照，即可查看變更內容。',
    emptyTitle: '還沒有快照',
    emptyBody: '上傳此資產的 lockfile 以建立第一份快照。',
    osEmptyBody: '上傳指令產生的檔案，以建立第一份快照。',
  },
  diff: {
    title: '快照之間的變更',
    summary:
      '#{{from}} → #{{to}}：新增 {{added}}、移除 {{removed}}、變更 {{upgraded}}、未變 {{unchanged}}',
    change: '變更',
    versions: '版本',
    added: '新增',
    removed: '移除',
    upgraded: '變更',
  },
  dashboard: {
    description: '依優先順序與 SLA 整理的未處理漏洞。日期以 {{timeZone}} 計算。',
    scope: '儀表板範圍',
    allClients: '所有客戶',
    allProjects: '所有專案',
    open: '未處理',
    openByPriority: '依優先順序的未處理漏洞',
    noOpenTitle: '沒有未處理的漏洞',
    noOpenBody: '目前沒有需要修補的項目。',
    topProjects: '最緊急漏洞最多的專案',
    trendTitle: '近 90 天的未處理漏洞',
    trendDescription: '每天結束時的未處理漏洞數，每個優先順序一張小圖。',
    trendScale: '所有小圖使用相同刻度，從 0 到 {{max}}。',
    trendSummary: '{{priority}}：{{days}} 天前 {{first}} 筆未處理，目前 {{last}} 筆。',
    trendDelta: '90 天內 {{delta}}',
    trendFlat: '90 天內沒有變化',
    trendTable: '以表格顯示數字',
    date: '日期',
    noTrendTitle: '還沒有歷史資料',
    noTrendBody: '上傳第一份套件清單後開始記錄趨勢。',
    sla: 'SLA 狀態',
    slaLink_one: '{{count}} 筆漏洞{{state}}',
    slaLink_other: '{{count}} 筆漏洞{{state}}',
    allFixes: '所有最近修補',
    slaHint: '「即將到期」表示距離到期日 2 天以內。',
    recentFixes: '最近修補',
    noFixesTitle: '還沒有修補紀錄',
    noFixesBody: '當較新的套件清單不再包含有漏洞的版本時，Smoke 會將漏洞標記為已修補。',
    onboardTitle: '只要三個步驟就能看到第一份報告',
    onboardBody: 'Smoke 會將你的 lockfile 與公開漏洞公告比對，並告訴你該先修補什麼。',
    onboardAction: '新增客戶',
    step1: '建立客戶，例如「ACME Corp」。',
    step2: '新增專案與資產。',
    step3: '上傳 package-lock.json、composer.lock 或其他 lockfile。',
  },
  eol: {
    title: '停止支援通知',
    description: '已停止或即將停止支援的執行環境、框架與作業系統，與 P1–P4 分開列出。',
    projectDescription: '根據這個專案的技術堆疊。',
    emptyTitle: '沒有停止支援通知',
    emptyBody: '技術堆疊由資產自動產生，其中的項目停止支援時就會收到通知。',
    emptyProjectTitle: '沒有停止支援通知',
    emptyProjectBody: '這個專案的技術堆疊目前沒有已停止或即將停止支援的項目。',
    headline: {
      eol: '{{product}} 已停止支援（自 {{date}} 起）',
      soon: '{{product}} 將於 {{date}} 停止支援',
      other: '{{product}}',
    },
    latest: '最新版本 {{version}}',
    releaseNotes: '版本說明',
    advice: {
      runtime: '建議升級到 {{target}}。先確認相依套件支援新版本，再更新版本設定和 CI。',
      framework: '建議升級到 {{target}}。跨多個主要版本時請逐版升級，每次都跑測試。',
      os: '建議規劃升級或遷移到 {{target}}：確認上面的軟體支援新版本，先在測試環境驗證，備份後再升級。',
      database:
        '建議升級到 {{target}}：先備份並確認官方升級路徑，在測試環境驗證後再升級正式資料庫。',
      service: '建議升級到 {{target}}：先確認設定相容，在測試環境驗證後再升級。',
    },
    adviceUntil: '支援到 {{date}}。',
    adviceNone: '目前查不到更新的支援版本，請查看官方公告，並評估替代方案。',
    promptGenerate: '產生升級提示詞',
    promptRegenerate: '重新生成提示詞',
    promptView: '查看提示詞',
    promptViewOld: '查看舊版',
    promptCopy: '複製提示詞',
    promptTitle: '將 {{product}} 升級到 {{target}}',
    promptCaption:
      '產生於 {{date}}，由 {{name}} 產生。可貼到 Claude Code、Cursor、GitHub Copilot 或 Codex。',
    promptOutdatedTitle: '提示詞已過期',
    promptOutdatedBody: '停止支援資訊或建議版本在產生後有變更，建議重新生成。舊版會保留。',
    promptGoneBody: '這個項目已不在技術堆疊中。這份提示詞保留備查。',
    promptGoneTitle: '已不在技術堆疊的提示詞',
    promptOthers: '其他版本',
  },
  findings: {
    title: '漏洞發現',
    description: '依緊急程度排序。篩選條件會保留在網址中，方便分享。',
    moreAdvisories_one: '另 {{formatted}} 則',
    moreAdvisories_other: '另 {{formatted}} 則',
    count_one: '{{count}}{{more}} 筆漏洞發現',
    count_other: '{{count}}{{more}} 筆漏洞發現',
    total_one: '共 {{count}} 筆漏洞發現 · 已顯示 {{shown}} 筆',
    total_other: '共 {{count}} 筆漏洞發現 · 已顯示 {{shown}} 筆',
    views: '檢視',
    viewAll: '所有漏洞發現',
    viewFixed: '最近修補',
    clearFilters: '清除 {{count}} 個篩選條件',
    selectAll: '選取所有顯示中的漏洞發現',
    select: '選取 {{name}}',
    selected_one: '已選取 {{count}} 筆',
    selected_other: '已選取 {{count}} 筆',
    bulkLabel: '對選取項目執行操作',
    bulkDone: '已更新 {{updated}} 筆，略過 {{skipped}} 筆',
    assign: '指派',
    snooze: '暫緩',
    snoozeBody: '到了該日期，漏洞發現會重新回到未處理。',
    snoozeUntil: '暫緩至',
    ignore: '忽略',
    ignoreBody:
      '已忽略的項目仍可透過狀態篩選查看。同一版本出現新的漏洞公告時，會建立新的漏洞發現。',
    ignoreReason: '原因',
    ignoreExpiry: '重新開啟日期',
    ignoreExpiryHint: '選填。漏洞發現會在此日期重新開啟。',
    note: '備註',
    combinedRule: '合併提示：最多 {{max}} 筆，且須屬於同一個資產。',
    clearSelection: '取消選取',
    emptyTitle: '沒有漏洞發現',
    emptyBody: '上傳套件清單後，漏洞會顯示在這裡。',
    emptyFilteredTitle: '沒有符合篩選條件的漏洞發現',
    emptyFilteredBody: '請調整或清除篩選條件。',
  },
  filters: {
    label: '篩選漏洞發現',
    any: '全部',
    package: '套件',
    advisory: '漏洞編號',
    search: '搜尋',
    client: '客戶',
    project: '專案',
    asset: '資產',
    selectedAsset: '已選取的資產',
    unresolved: '尚未解決（預設）',
    allStatuses: '所有狀態',
    ecosystem: '生態系',
    scope: '範圍',
    kevYes: '已知遭利用',
    kevNo: '不在 KEV 中',
    assignedToMe: '指派給我',
    unassigned: '未指派',
  },
  finding: {
    kernelState: {
      old: '未執行的舊 kernel',
      oldTitle: '已安裝但沒有在執行，一律列為 P4；請移除它',
      newer: '尚未執行的新 kernel',
      newerTitle: '已安裝但尚未執行，重新開機後才會使用',
    },
    distroWontFix: '發行版不修',
    distroWontFixTitle: '發行版將所有漏洞公告標示為不修補，只放在摘要通知中。',
    priority: '優先順序',
    package: '套件',
    where: '專案／資產',
    risk: '風險',
    fixState: '修補',
    status: '狀態',
    sla: 'SLA',
    fixedAt: '修補日期',
    assignee: '負責人',
    transitive: '間接相依',
    direct: '直接相依',
    kevTitle: '列於 CISA 已知遭利用漏洞清單（KEV）',
    advisoryCount_one: '{{count}} 則漏洞公告',
    advisoryCount_other: '{{count}} 則漏洞公告',
    details: '詳細資料',
    ecosystem: '生態系',
    scope: '範圍',
    relationship: '相依關係',
    severity: '嚴重程度',
    slaFix: '修補 SLA',
    slaMitigate: '緩解或接受風險',
    firstSeen: '首次發現',
    lastSeen: '最近一次發現',
    priorityReason: '為什麼是這個優先順序',
    finalPriority: '結果：{{priority}}',
    paths: '安裝位置',
    manifest: 'Manifest',
    dependencyPaths: '相依路徑',
    actions: '操作',
    transition: {
      open: '重新開啟',
      in_progress: '開始處理',
      pending_verification: '標記為已修補（待驗證）',
    },
    markMitigated: '記錄已套用的緩解措施',
    reportFalsePositive: '回報誤判',
    falsePositiveBody: 'Smoke 人員會審核此回報，在那之前漏洞發現維持不變。',
    falsePositiveScope_one: '此回報涵蓋此漏洞發現的 {{count}} 則有效漏洞公告。',
    falsePositiveScope_other: '此回報涵蓋此漏洞發現的 {{count}} 則有效漏洞公告。',
    falsePositiveQueue: '送入審核佇列',
    falsePositiveSent: '感謝回報，已送入審核佇列。',
    sendReport: '送出回報',
    fixedBySmoke: '只有 Smoke 能將漏洞標記為已修補：當較新的套件清單不再包含有漏洞的版本時。',
    statusChanged: '狀態已變更為「{{status}}」',
    customSourceTitle: '可能是私有套件',
    customSourceBody:
      '此套件是從公開預設以外的 registry 安裝。它可能是同名的私有套件，若是如此，此漏洞發現並不適用。',
    inventoryGaps: '套件清單中有無法解析的項目，Smoke 可能無法看到所有套件。請參考資產的解析報告。',
    ignoredTitle: '已忽略：{{reason}}',
    reopensOn: '將於 {{date}} 重新開啟。',
    snoozedUntil: '暫緩至 {{date}}。',
    mitigatedTitle: '已緩解，但仍在安裝中',
    mitigatedBody: '已套用緩解措施，但有漏洞的版本仍然安裝中。在修補前，仍計入未處理數量。',
    fixedBody: '已修補（已驗證）{{date}}：較新的套件清單已不再包含有漏洞的版本。',
    pendingBody: '等待驗證：上傳更新後的 lockfile，或透過 MCP 提交，Smoke 會確認修補結果。',
  },
  manualPriority: {
    badge: '手動 P1',
    raise: '調成 P1',
    raiseTitle: '把 {{id}} 調成 P1',
    raiseBody:
      '這個工作區所有受影響的資產都會變成 P1，之後新增的資產也會自動套用。SLA 從現在起算，並會發出升級通知。沒有在執行的 kernel 維持 P4。',
    reason: '原因',
    reasonHint: '為什麼這個 CVE 要優先處理。原因會顯示給看得到這筆漏洞的成員，並寫入稽核紀錄。',
    raised: '已把 {{id}} 調成 P1。',
    by: '{{name}} 於 {{date}} 調成 P1',
    unknown: '已移除的使用者',
    kernelStaysP4: '這個 kernel 沒有在執行，這筆維持 P4。',
    cancel: '取消手動 P1',
    cancelTitle: '取消 {{id}} 的手動 P1？',
    cancelBody: '優先順序和 SLA 期限會回到規則算出的結果。',
    cancelled: '已取消 {{id}} 的手動 P1。',
    title: '手動調成 P1 的 CVE',
    description:
      '由 Admin 判斷要優先處理的漏洞，套用到整個工作區。要調整時，到漏洞發現的公告清單操作。',
    emptyTitle: '沒有手動調成 P1 的 CVE',
    emptyBody: '優先順序目前都由規則計算。',
    advisory: '漏洞編號',
    raisedBy: '操作者',
    openFindings: '未處理的漏洞',
  },
  advisory: {
    title_one: '{{count}} 則漏洞公告',
    title_other: '{{count}} 則漏洞公告',
    aliases: '其他編號',
    percentile: '百分位 {{value}}',
    references: '{{count}} 個參考連結（第三方）',
    withdrawn: '已撤回',
    thirdParty: '公告內文（第三方內容，未經 Smoke 審閱）',
    thirdPartyLabel: '第三方公告內文',
    showDetails: '顯示詳細內容',
    truncated: '內容已截短，完整內容請見下方的公告連結。',
    noFixYet: '尚無修補',
    source: '資料來源：',
    sourceSeparator: '、',
    processedBySmoke: '，經 Smoke 整理。',
    epssByFirst: 'EPSS 分數由 FIRST 提供。',
    urgentTitle: '最需要優先處理的漏洞公告',
    urgentDescription:
      '依手動 P1、KEV、嚴重度、CVSS 分數和 EPSS 自動排序：{{total}} 則中最前面的 {{shown}} 則。',
    viewAll: '查看全部',
    searchLabel: '搜尋漏洞編號',
    sortLabel: '排序方式',
    sortUrgency: '依綜合緊急程度',
    sortCvss: '依 CVSS 分數',
    sortEpss: '依 EPSS',
    filterPriority: '只看 {{priority}}（{{count}} 則）',
    showMore_one: '顯示更多（還有 {{formatted}} 則）',
    showMore_other: '顯示更多（還有 {{formatted}} 則）',
    noMatch: '沒有符合的漏洞公告。',
    loadText: '載入公告內容',
    textUnavailable: '目前無法取得公告內容。',
  },
  fix: {
    distroCandidates: '修補版本（來自發行版的安全資料）',
    title: '修補可用性',
    stale: '最後已知狀態；Smoke 暫時無法更新',
    staleBody: '暫時無法連線到 smoke-signal。這是 {{date}} 的狀態，Smoke 會自動重試。',
    coverage: '升級可修補 {{total}} 則漏洞公告中的 {{covered}} 則。',
    packageManager: '套件管理工具：{{name}}',
    overrideSupported: '支援覆寫（{{mechanism}}）',
    overrideUnsupported: '不支援覆寫',
    checkedAt: '檢查於 {{date}}',
    candidates: '已修補的版本（已在 registry 確認）',
    version: '版本',
    delta: '變更幅度',
    published: '發布日期',
    fixes: '修補',
    fixesCount: '{{total}} 則中的 {{count}} 則',
    deprecated: '已棄用',
    prerelease: '預發布',
    noCandidates: '目前沒有已發布的版本能修補這些漏洞。Smoke 每天檢查，一有修補就會通知你。',
    parents: '上層套件',
    parentRefresh: '目前的版本範圍已允許修補版本：重新產生 lockfile 即可',
    parentUpgrade: '升級到 {{versions}}',
    parentNone: '目前沒有上層套件版本允許修補版本',
    uncheckedParents_one: '另有 {{count}} 個上層套件未檢查。',
    uncheckedParents_other: '另有 {{count}} 個上層套件未檢查。',
  },
  remediation: {
    aiLinuxTitle: 'Linux 套件的 AI 分析尚未開啟',
    aiLinuxBody: '上方步驟由 Smoke 產生，不需要 AI 分析也完整可用。',
    title: '修復建議',
    description: '步驟由 Smoke 依套件管理工具產生。套件名稱、版本與指令永遠不會來自 AI。',
    target: '目標：{{pkg}} {{version}}',
    file: '檔案：{{file}}',
    alternatives: '其他做法（{{count}}）',
    noDeterministic: '沒有可用的升級途徑，請參考緩解措施。',
    aiTitle: 'AI 分析',
    aiUnavailable: 'AI 分析暫時無法使用',
    aiUnavailableBody: '上方步驟不需要 AI 分析也完整可用。Smoke 稍後會重試分析。',
    aiQueued: '準備中，完成後此頁會自動更新。',
    aiNotGenerated:
      'AI 分析會補充破壞性變更風險、可利用性和程式碼修改提示，並併入修復提示。按下才會產生，會使用 AI 額度。',
    aiGenerate: '產生 AI 分析',
    aiRegenerate: '重新生成',
    aiGeneratedAt: '產生於 {{date}}',
    aiOutdatedTitle: '這份內容已過期',
    aiOutdatedBody: '建議重新生成。舊內容會保留，可以在版本紀錄查看。',
    aiRegenerateFailed: '上次重新生成沒有成功，請稍後再試。',
    versionsTitle: '版本紀錄',
    versionsEmpty: '還沒有產生過。',
    versionCurrent: '目前版本',
    versionOutdated: '已過期',
    versionAutomatic: '自動產生',
    versionView: '查看',
    versionTitle: '{{date}} 的版本',
    versionNoPrompt: '這個版本沒有保存修復提示。',
    breakingRisk: '破壞性變更風險',
    exploitability: '可利用性',
    restartImpact: '重啟影響',
    containerRedeploy: '容器不需要重啟服務：以新的映像檔重新部署容器即可。',
    aiBaseImageEdit: '在資產頁填寫或修改基底映像檔',
    aiPublicServices:
      'AI 只收到發行版套件提供的服務名稱；自訂服務不會送出，請以上方 Smoke 的步驟為準。',
    baseImageAdvice: '基底映像檔建議',
    maintenanceNotes: '維護注意事項',
    codeHints: '程式碼修改提示',
    unsourcedHint: '沒有版本說明或漏洞公告佐證：請對照套件文件確認',
    verification: '驗證重點',
  },
  rating: {
    question: '這份修復建議有幫助嗎？',
    helpful: '有幫助',
    notHelpful: '沒有幫助',
    reason: '哪裡有問題？',
    submit: '送出',
    thanks: '感謝你的回饋。',
  },
  mitigation: {
    title: '暫時緩解措施',
    intro: {
      none: '目前沒有修補版本',
      blocked: '已有修補版本，但此專案暫時無法升級到該版本',
      partial: '升級只能修補部分漏洞公告',
    },
    introBody: '在能安裝修補版本之前，這些做法可以降低風險。一有修補版本，Smoke 會立即通知你。',
    partialCoverage: '升級可修補 {{total}} 則漏洞公告中的 {{covered}} 則，其餘由以下緩解措施涵蓋。',
    noSourced: '這些漏洞公告目前沒有來自公開來源的緩解措施。',
    group: {
      sourced: '來自公開來源',
      ai_suggestion: 'AI 建議',
      generic: '通用緩解措施',
    },
    label: {
      generic: '通用',
      aiSuggestion: 'AI 建議，無來源引述',
      aiSuggestionQuoted: 'AI 建議，引自漏洞公告',
      aiSummaryUnreviewed: 'AI 整理自公開來源，尚未經人工審核',
      englishOnly: '此項目目前只有英文版',
      reviewed: '已審核',
    },
    impact: '營運影響：{{level}}',
    official: '官方',
    preconditions: '前提條件',
    quotedFrom: '出自 {{advisory}}（{{field}}）',
    advisoryField: { summary: '摘要', details: '詳細說明' },
    inferred: '由公告內容推論，公告本身沒有直接這樣寫。',
    conflicts: '來源說法不一致',
    peopleOnly: '僅供人員參考：不會放入程式碼代理的提示中。',
    other: '其他緩解措施',
    otherNote: '你採取了什麼做法？',
    reviewAt: '複查日期',
    reviewAtHint: 'Smoke 會在此日期提醒你（預設 30 天）。',
    apply_one: '標記已套用 {{count}} 項緩解措施',
    apply_other: '標記已套用 {{count}} 項緩解措施',
    applied: '已記錄緩解措施，此漏洞發現已標記為已緩解。',
    appliedTitle: '已套用的緩解措施',
    reviewOn: '複查日期：{{date}}。',
  },
  prompt: {
    copy: '複製修復提示',
    copyMitigation: '複製緩解任務',
    preview: '預覽提示',
    title: '修復提示',
    mitigationTitle: '緩解任務',
    combined: '複製合併提示',
    combinedTitle: '合併修復提示',
    templateVersion: '範本 {{version}}。可貼到 Claude Code、Cursor、GitHub Copilot 或 Codex。',
    skipped_one: '有 {{count}} 筆漏洞發現沒有提示，已略過。',
    skipped_other: '有 {{count}} 筆漏洞發現沒有提示，已略過。',
  },
  history: {
    title: '歷程',
    smoke: 'Smoke',
    unassigned: '未指派',
    snapshot: {
      title: '完成時的處理狀況',
      assignee: '負責人',
      priority: '優先順序',
      package: '套件',
      asset: '資產',
      advisories: '漏洞',
      more: '等另外 {{count}} 筆',
      sla: 'SLA',
      slaMet: '如期完成（期限 {{date}}）',
      slaMissed: '逾期完成（期限 {{date}}）',
      mitigations: '緩解措施',
    },
  },
  people: {
    left: '{{name}}（已離開）',
    deleted: '已刪除的帳號',
  },
  settings: {
    title: '工作區設定',
    description: '名稱、時區、安全性與 SLA。',
    general: '一般',
    adminOnly: '只有工作區管理員可以變更這些設定。',
    enforce2fa: '要求雙重驗證',
    enforce2faHint: '僅限擁有者設定。尚未啟用的成員必須先啟用才能繼續使用。',
    dangerZone: '危險區域',
    deleteWorkspace: '刪除工作區',
    deleteWorkspaceBody:
      '僅限擁有者。所有人會立即失去存取權，所有 API 權杖都會撤銷，資料會在 30 天內清除。',
    deleteWorkspaceTitle: '確定要刪除「{{name}}」嗎？',
    billingOwner: {
      title: '帳務擁有者',
      description:
        '這個工作區計入帳務擁有者帳號的方案與用量。帳務擁有者必須是擁有者；交給另一位擁有者時，對方的方案必須容納得下這個工作區的專案與資產。',
      you: '{{name}}（你）',
      none: '目前沒有帳務擁有者，適用免費方案的限制。',
      newOwner: '交給',
      transfer: '交出帳務',
      noCandidates: '沒有其他擁有者可以接手。請先在成員頁面把成員設為擁有者。',
      confirmTitle: '把帳務交給 {{name}}？',
      confirmBody: '交出後，這個工作區改用 {{name}} 的方案與用量，你的方案不再涵蓋它。',
      done: '已把帳務交給 {{name}}。',
    },
    deleteWorkspaceBilling:
      '刪除後，這個工作區不再計入帳務擁有者的方案用量；方案本身不會取消，要取消請到帳務頁面。已付款項不會自動退款。',
    typeName: '輸入工作區名稱以確認',
    reason: '原因（選填）',
    deleteWorkspaceConfirm: '刪除工作區',
    workspaceDeleted: '工作區已排定刪除。',
  },
  sla: {
    title: 'SLA 政策',
    description: '各優先順序的修補（無修補版本時為緩解）天數。',
    days: '天',
    range: '請輸入 1 到 365',
    recompute: '儲存後，未處理漏洞的到期日會重新計算。',
    saved: '已儲存 SLA 政策，到期日已重新計算。',
  },
  tokens: {
    oauthClient: '用戶端',
    lastClient: '最近由 {{name}} 使用',
    projects: '專案',
    allProjects: '所有專案',
    restrictProjects: '限定專案',
    restrictProjectsHint: '選填。未選擇時，權杖可存取你有權限的所有專案。',
    oauthNote: '由程式碼代理透過 OAuth 登入時建立。撤銷即可中斷該代理的連線。',
    title: 'API 權杖',
    description: '供 CLI、CI 與 MCP 伺服器使用。權杖只以雜湊值保存，最長一年後到期。',
    new: '新增權杖',
    name: '名稱',
    kind: '類型',
    kindHint: '專案權杖只能存取該專案。',
    scopes: '權限範圍',
    prefix: '權杖',
    lastUsed: '最近使用',
    expires: '到期日',
    expiresIn: '有效天數',
    expiresHint: '1 到 365 天。',
    never: '從未',
    revoked: '已撤銷',
    revoke: '撤銷',
    confirmRevoke: '要撤銷「{{name}}」嗎？使用此權杖的服務將無法運作。',
    rename: '改名',
    renameTitle: '權杖改名',
    renameHint: '只改顯示的名稱，權杖本身、權限和到期日不變，使用中的服務不受影響。',
    shownOnce: '請立即複製權杖，它只會顯示這一次。',
    done: '我已複製',
    chooseProject: '選擇專案',
    emptyTitle: '沒有 API 權杖',
    emptyBody: '建立權杖，即可從 CI 或程式碼代理使用 Smoke。',
  },
  mcp: {
    open: '程式碼代理設定',
    chooseProject: '選擇此儲存庫所屬的專案',
    title: '程式碼代理設定（MCP）',
    description:
      '將 Claude Code、Cursor、GitHub Copilot 或 Codex 連接到 Smoke。代理可以列出漏洞發現、取得修復任務並提交新的 lockfile，再由 Smoke 驗證修補結果。',
    serverTitle: 'Smoke MCP 伺服器',
    serverUrl: '伺服器網址',
    authBody:
      '代理透過 OAuth 登入：第一次使用時，瀏覽器會開啟 Smoke，讓你核准存取某個工作區。設定片段中不含任何機密。',
    asset: '此儲存庫對應的資產',
    assetHint: '會寫入 .smoke/project.json，讓代理知道要更新哪一份套件清單。',
    chooseAsset: '選擇資產',
    noAssets: '為此專案新增資產後，就能取得 .smoke/project.json 檔案。',
    projectFileTitle: '.smoke/project.json',
    projectFileBody: '請將此檔案提交到儲存庫，內容只有工作區與資產的 ID。',
    agents: '程式碼代理',
    installInCursor: '加入 Cursor',
    installHint: '開啟 Cursor 並安裝 Smoke MCP 伺服器。',
    command: '指令',
    files: '檔案',
    copyFile: '複製檔案',
    next: '接著',
    tokenAlternative: '改用 API 權杖而非 OAuth',
    tokenAlternativeBody:
      '適用於不支援 OAuth 的用戶端。請在「API 權杖」建立個人權杖，並放在 SMOKE_TOKEN 環境變數中；切勿提交到儲存庫。',
    templateVersion: '範本 {{version}}',
    message: {
      claude_code: {
        authenticate:
          '在 Claude Code 中執行 {{command}}，選擇「{{server}}」並點選 Authenticate，接著在瀏覽器登入 Smoke 並選擇工作區。',
        run: '然後輸入 {{command}}。',
      },
      cursor: {
        sign_in:
          '點選「加入 Cursor」（或新增 {{file}}），並在 Cursor 要求時登入 Smoke。登入前，設定 → MCP 會顯示「Needs login」。',
        run: '在代理對話中提及 {{mention}}。',
      },
      github_copilot: {
        trust_and_sign_in: '第一次啟動時，VS Code 會請你信任此伺服器並登入 Smoke（Agent 模式）。',
        run: '在 Copilot Chat（Agent 模式）中輸入 {{command}}。',
      },
      codex: {
        login: '執行 {{command}}，並在瀏覽器登入 Smoke。',
        project_config: '受信任的專案可以改用 {{projectFile}}，而不是 {{userFile}}。',
      },
    },
  },
  setupMode: {
    create: '新檔案',
    merge: '合併到既有檔案',
  },
  audit: {
    title: '稽核記錄',
    description: '此工作區中與安全相關的操作。',
    time: '時間',
    action: '操作',
    actor: '執行者',
    target: '對象',
    reason: '原因',
    emptyTitle: '還沒有紀錄',
  },
  assignments: {
    title: '指派的成員',
    members: '成員',
    clientTitle: '指派到 {{name}} 的成員',
    clientHelp: '被指派的成員會收到此客戶所有專案的通知。沒有指派任何人時，由擁有者與管理員接收。',
    projectHelp:
      '被指派的成員會收到此專案的通知，指派到其客戶的成員也會收到。沒有指派任何人時，由擁有者與管理員接收。',
    none: '尚未指派任何人。',
    since: '自 {{date}}',
    add: '指派成員',
    choose: '選擇成員',
    assign: '指派',
    removeFor: '移除 {{name}}',
  },
  notifications: {
    title: '通知',
    description: '設定 Smoke 在何時、透過哪些管道通知你漏洞、SLA 期限、修補與緩解措施。',
    tab: {
      mine: '我的通知',
      workspace: '工作區',
      history: '傳送紀錄',
    },
    chatsTitle: '已連結的帳號',
    chatsBody: '聊天帳號連結屬於你個人，適用於你所有的工作區。',
    active: '使用中',
    emailUnverified: '尚未驗證',
    linked: '已連結',
    notLinked: '未連結',
    notConfigured: '尚未開放',
    notConfiguredBody: '這台 Smoke 伺服器尚未連接此聊天服務。Email 通知不受影響。',
    linkedSince: '於 {{date}} 連結。',
    unlink: '解除連結',
    unlinked: '已解除連結。',
    lineStep1: '將 Smoke 的 LINE 官方帳號加為好友（按鈕或 QR code）。',
    lineStep2: 'Smoke 會在 LINE 傳送連結給你，開啟後在此登入並確認。',
    lineAddFriend: '在 LINE 加入 Smoke',
    lineQr: '加入 Smoke LINE 好友的 QR code',
    lineNoFriendUrl: '在 LINE 搜尋 Smoke 的官方帳號並加為好友。',
    telegramIntro: '取得一次性連結，開啟與 Smoke Telegram 機器人的對話。',
    telegramLink: '連結 Telegram',
    telegramOpen: '在 Telegram 開啟連結，並在與 @{{bot}} 的對話中按下 Start。',
    telegramOpenButton: '開啟 Telegram',
    telegramQr: 'Telegram 連結的 QR code',
    copyLink: '複製連結',
    expiresAt: '連結只能使用一次，將於 {{time}} 失效。',
    waiting: '連結使用後，此頁面會自動更新。',
    newLink: '取得新連結',
    mineTitle: '我在此工作區的通知',
    mineBody:
      '標示「預設」的列沿用工作區預設值；修改後即為你的個人設定。收件人為指派者，以及被指派到該專案或客戶的成員。',
    byPriority: '各優先順序的管道與時機',
    timing: '時機',
    timingFor: '{{priority}} 的通知時機',
    channelFor: '{{priority}} 使用 {{channel}}',
    source: '預設或自訂',
    custom: '自訂',
    default: '預設',
    notOnPlan: '目前方案不支援',
    planChannels: '目前方案僅提供 Email 通知。P1 警示一律立即傳送。',
    otherTitle: '其他通知',
    emailTitle: 'Email',
    emailHelp: '取消勾選即停止收到該類 Email。每封 Email 也都附有取消訂閱連結。',
    save: '儲存',
    saved: '通知設定已儲存。',
    resetDefaults: '改用工作區預設值',
    defaultsTitle: '工作區預設值',
    defaultsBody:
      '適用於尚未自訂設定的成員。每日摘要於 {{time}}（{{timeZone}}）寄出，每週摘要於{{day}}寄出。',
    saveDefaults: '儲存預設值',
    defaultsSaved: '工作區預設值已儲存。',
    groupsTitle: '群組聊天',
    groupsBody:
      '依下方轉送規則接收此工作區通知的 Telegram 群組。訊息語言採用連結群組的管理員的語言。',
    noGroups: '尚未連結任何群組。',
    groupNotConfigured: '這台 Smoke 伺服器尚未連接 Telegram，因此無法連結群組。',
    groupLink: '連結 Telegram 群組',
    groupStep1: '將 Smoke 機器人加入群組（使用下方按鈕，或從群組設定加入）。',
    groupStep2: '在群組中傳送這個指令：',
    groupAddBot: '將機器人加入群組',
    untitledGroup: '未命名群組',
    routingTitle: '轉送到群組',
    routingBody: '將某個客戶（或所有客戶）的通知轉送到群組。未選擇類別表示所有類別。',
    routingNeedsGroup: '請先連結群組。',
    noRoutes: '沒有規則：群組不會收到任何通知。',
    rule: '規則 {{n}}',
    everyClient: '所有客戶',
    groupChat: '群組',
    minPriority: '優先順序',
    upTo: {
      P1: '僅 P1',
      P2: 'P1 – P2',
      P3: 'P1 – P3',
      P4: '全部（P1 – P4）',
    },
    routeCategories: '僅限這些類別',
    addRule: '新增規則',
    saveRouting: '儲存轉送規則',
    routingSaved: '轉送規則已儲存。',
    historyTitle: '傳送紀錄',
    historyBody: 'Smoke 已傳送或嘗試傳送的所有通知，最新的在最前面。',
    status: '狀態',
    channel: '管道',
    time: '時間',
    recipient: '收件人',
    what: '內容',
    formerMember: '已離開的成員',
    findingCount_one: '{{count}} 筆漏洞發現',
    findingCount_other: '{{count}} 筆漏洞發現',
    attempts_one: '嘗試 {{count}} 次',
    attempts_other: '嘗試 {{count}} 次',
    noDeliveriesTitle: '尚未傳送任何通知',
    noDeliveriesBody: 'Smoke 傳送通知後會顯示在這裡。',
  },
  lineLink: {
    title: '連結你的 LINE 帳號',
    subtitle: '在 LINE 接收 Smoke 通知。',
    body: '要將開啟此連結的 LINE 帳號連結到你的 Smoke 帳號 {{email}} 嗎？',
    warning:
      '請只在你自己從 Smoke 的 LINE 訊息開啟此連結時繼續。該 LINE 帳號的擁有者將會收到你的通知。',
    confirm: '連結 LINE 帳號',
    redirecting: '正在開啟 LINE…',
    invalid: '此連結無效，請從 Smoke 的 LINE 訊息重新開啟。',
    back: '前往 Smoke',
  },
  unsubscribe: {
    title: '取消訂閱',
    body: '要停止收到以下 Email 嗎：{{list}}？',
    bodyWorkspace: '要停止收到工作區 {{workspace}} 的「{{list}}」Email 嗎？',
    confirm: '取消訂閱',
    doneTitle: '已取消訂閱',
    doneBody: '你將不再收到以下 Email：{{list}}。其他通知不受影響。',
    manage: '要變更所有通知設定，請',
    signIn: '登入 Smoke',
  },
  os: {
    title: '作業系統',
    description: '來自目前的套件清單。',
    distro: '發行版',
    kernel: 'Kernel',
    packages: '已安裝套件',
    packageCount_one: '{{formatted}} 個套件（{{manager}}）',
    packageCount_other: '{{formatted}} 個套件（{{manager}}）',
    services: '執行中的服務',
    serviceCount_one: '{{count}} 個服務',
    serviceCount_other: '{{count}} 個服務',
    collectedAt: '收集時間',
    staleTitle_one: '套件清單已是 {{count}} 天前收集的',
    staleTitle_other: '套件清單已是 {{count}} 天前收集的',
    staleBody: '主機更新後套件會改變，比對結果可能已不準確。請按「用指令收集」重新執行指令並上傳。',
    rebootRequired: '主機顯示需要重新開機（/var/run/reboot-required）。',
    unsupportedTitle: '不支援此版本',
    unsupportedBody: 'Smoke 沒有 {{release}} 的漏洞資料，因此沒有進行比對。',
    unsupportedBodyUnknown: 'Smoke 沒有此版本的漏洞資料，因此沒有進行比對。',
    supportedReleases:
      '支援的版本：Ubuntu 22.04、24.04、26.04 LTS；Debian 12、13；Alpine 3.20 至 3.24。',
  },
  ciUpload: {
    title: 'CI 自動上傳',
    description:
      '每次推送後，由 GitLab CI 或 GitHub Actions 自動把套件清單送到 Smoke，不用再手動上傳。照下面四步完成，內容都已經準備好，複製貼上即可。',
    open: '設定 CI 上傳',
    openAgain: '查看設定步驟',
    notSetUp: '這個資產還沒收到過 CI 上傳。',
    stateOn: '已設定',
    stateWaiting: '等待第一次上傳',
    stateNoToken: '權杖已失效',
    listBadge: '{{ci}} {{state}}',
    noActiveToken:
      '這個資產沒有可用的上傳權杖（已過期或已撤銷），CI 上傳會被拒絕。請建立新權杖，並更新 CI 的 SMOKE_TOKEN。',
    expiringSoon:
      '權杖「{{name}}」將於 {{date}} 到期。請先建立新權杖並更新 CI 的 SMOKE_TOKEN，再撤銷舊的。',
    lastUpload: '最後一次 CI 上傳',
    lastUploadValue: '{{date}}，來自 {{source}}',
    noUploadYet: '還沒收到。推送後，確認 pipeline 或工作流程是否成功。',
    tokens: '上傳權杖',
    tokenExpires: '{{date}} 到期',
    tokenUsed: '最後使用 {{date}}',
    tokenUnused: '尚未使用',
    provider: '使用的 CI',
    step1: '建立這個資產專用的上傳權杖',
    createToken: '建立權杖',
    existing:
      '這個資產已經有「{{name}}」權杖，{{date}} 到期。再建立一個不影響舊的，換好後可以到 API 權杖撤銷舊的。',
    adminOnly: '權杖只能由管理員建立，請管理員來完成這一步。',
    tokenTitle: '權杖只會顯示這一次',
    key: '變數',
    value: '值',
    tokenPending: '完成第 1 步後會顯示在這裡',
    branch: '要上傳的分支',
    branchHint: '填實際部署的分支；留空表示預設分支。',
    badBranch: '分支名稱只能有英數字和 . _ / @ + -',
    paths: '套件清單的路徑（一行一個）',
    pathsHint: '相對於儲存庫根目錄。每次都要列出全部，沒列到的會當成已移除。',
    pathsFromSnapshot: '已帶入最近一次上傳的檔案。每次都要列出全部，沒列到的會當成已移除。',
    badPaths: '這些路徑有不支援的字元：{{paths}}',
    step4: '推送後確認',
    manageTokens: '管理 API 權杖',
    gitlab: {
      tokenNote:
        '請直接複製到下一步的 GitLab 變數，不要存在其他地方。權杖只能上傳這個資產的套件清單，一年後到期。',
      step2: '在 GitLab 專案的 Settings → CI/CD → Variables 新增兩個變數',
      flags: '選項',
      urlFlag: '—',
      tokenFlag: '勾選 Masked',
      variablesHint: '如果也勾選 Protected，下面選的分支必須是受保護的分支，否則 job 讀不到權杖。',
      step3: '把這個 job 加到儲存庫根目錄的 .gitlab-ci.yml',
      verify:
        'Pipeline 跑完後，這個資產的快照紀錄會出現來源為「GitLab CI」的新快照。失敗時，job 會印出 Smoke 回傳的錯誤。',
    },
    github: {
      tokenNote:
        '請直接複製到下一步的 GitHub Secret，不要存在其他地方。權杖只能上傳這個資產的套件清單，一年後到期。',
      step2: '在 GitHub 儲存庫的 Settings → Secrets and variables → Actions 新增兩個設定',
      flags: '分頁',
      urlFlag: 'Variables',
      tokenFlag: 'Secrets',
      variablesHint: '權杖一定要放在 Secrets：Variables 的值會出現在執行記錄裡。',
      step3: '把這個檔案存成儲存庫裡的 .github/workflows/smoke-inventory.yml',
      verify:
        '工作流程跑完後（GitHub 的 Actions 分頁），這個資產的快照紀錄會出現來源為「GitHub Actions」的新快照。失敗時，步驟會印出 Smoke 回傳的錯誤。',
    },
  },
  collector: {
    title: 'Linux 收集程式',
    description: '每天自動上傳這台主機的套件清單，不必再手動執行指令。',
    notInstalled: '這台主機尚未安裝收集程式。',
    adminOnly: '工作區管理員可以安裝收集程式。',
    unpublishedTitle: '收集程式尚未發布',
    unpublished: '這個 Smoke 網站還沒有提供收集程式下載，目前請先用指令收集。',
    checkFailed: '無法確認收集程式是否可以下載，請稍後再試。',
    install: '安裝收集程式',
    reinstall: '產生新權杖與安裝指令',
    tokenTitle: '請立即複製權杖，它只會顯示這一次。',
    tokenNote: '只用於這台主機的資產權杖，只能上傳套件清單，一年後到期。',
    step1: '複製權杖。',
    step2: '在主機上執行下方的安裝指令，出現提示時貼上權杖。',
    step3: '收集程式會立即回報一次，之後每天回報。重新整理此頁即可看到狀態。',
    installIntro:
      '請以可使用 sudo 的帳號執行。安裝腳本會先驗證下載檔案的簽章再安裝，並從終端機讀取權杖，因此權杖不會顯示在畫面上，也不會留在 shell 歷史紀錄中。',
    dryRun: '想先確認會上傳哪些內容（不會送出）：',
    uninstall: '移除收集程式、設定檔與排程：',
    done: '完成',
    lastReport: '最後回報',
    version: '版本',
    versionValue: '{{version}}（{{arch}}）',
    unknown: '未知',
    tokenExpires: '權杖到期日',
    noActiveToken:
      '這台主機沒有有效的收集程式權杖，收集程式無法上傳。請產生新的權杖並重新執行安裝指令。',
    revoke: '撤銷或管理權杖',
    staleTitle_one: '收集程式已 {{count}} 天沒有回報',
    staleTitle_other: '收集程式已 {{count}} 天沒有回報',
    staleBody:
      '請確認主機上的 smoke-collector.timer（或 cron 排程）仍在執行，且主機可以連線到 Smoke。在主機上執行 smoke-collector run 可以看到錯誤原因。已通知管理員。',
    staleBadge_one: '收集程式已 {{count}} 天沒有回報',
    staleBadge_other: '收集程式已 {{count}} 天沒有回報',
  },
  osPaste: {
    open: '用指令收集',
    title: '用指令收集',
    hostDescription: '在主機上執行指令，結果會存成檔案。上傳該檔案，或貼上它的內容。',
    imageDescription: '對映像檔執行指令，結果會存成檔案。上傳該檔案，或貼上它的內容。',
    method: '收集方式',
    methodRun: '在映像檔中執行指令',
    methodSbom: '改用 SBOM',
    sbomIntro: '用 syft 或 trivy 產生映像檔的 CycloneDX SBOM。Smoke 會讀取其中的 deb 和 apk 套件。',
    sbomUpload: '指令會把 SBOM 存成 {{file}}，接著上傳這個檔案：',
    chooseSbom: '選擇 SBOM 檔案並上傳',
    uploading: '上傳中…',
    runOnHost: '1. 在主機上執行，結果會存成目前目錄的 {{file}}：',
    runInImage: '1. 對映像檔執行（需要 Docker），結果會存成目前目錄的 {{file}}：',
    thenUpload: '2. 選擇 {{file}}，或在下方貼上它的內容（可用 cat {{file}} 顯示）：',
    chooseFile: '選擇 {{file}}',
    imageHint: '把 {{placeholder}} 換成映像檔名稱和標籤，例如 nginx:1.27。',
    imagePlaceholder: '<映像檔>',
    copyCommand: '複製指令',
    collectsTitle: '指令收集的資料',
    collects: {
      osRelease: '/etc/os-release 的 ID 和 VERSION_ID（發行版與版本）',
      packages: '已安裝套件：名稱、版本、架構，以及原始碼套件名稱和版本',
      kernel: 'kernel 版本（uname -r）',
      services: '執行中的 systemd 服務名稱（用來提示要重啟哪些服務）',
      servicePackages:
        'Ubuntu、Debian 主機上，每個執行中服務載入了哪些套件（用來準確列出要重啟的服務；以 sudo 執行才讀得到大多數服務）',
      reboot: '/var/run/reboot-required 是否存在',
    },
    nothingElse:
      '為了找出服務載入的套件，指令會讀取程序在 /proc 的資訊和套件的檔案清單，但結果只寫入套件名稱。不會讀取其他檔案內容。結果是純文字，上傳前可以先看過。',
    output: '檔案內容',
    outputHint: '選擇檔案後內容會顯示在這裡，也可以直接貼上 {{file}} 的全部內容。上限 5 MB。',
    wrongStart: '內容必須以 {{header}} 這一行開頭，請使用指令產生的完整檔案。',
    tooLarge: '超過 5 MB。',
    submit: '上傳',
  },
  distro: {
    kernelState: {
      oldTitle: '這是沒有在執行的舊 kernel',
      oldBody:
        '主機目前執行 {{running}}。只有開機進入這個版本時，它的漏洞才有影響，所以一律列為 P4（即使列在 KEV）。請移除它，不需要升級。',
      newerTitle: '這個較新的 kernel 尚未執行',
      newerBody:
        '主機目前執行 {{running}}，重新開機後才會使用這個版本。在那之前列為 P4；重新開機並再次上傳清單後，會依它的漏洞重新計算。',
    },
    title: '發行版套件',
    release: '版本',
    packageManager: '套件管理工具',
    sourcePackage: '原始碼套件',
    priority: '發行版優先順序',
    fixedVersions: '修補版本',
    noFixedVersion: '此版本尚無修補版本',
    binaries_one: '由此建置、已安裝的套件 {{count}} 個',
    binaries_other: '由此建置、已安裝的套件 {{count}} 個',
    arch: '架構',
    subscription: '需要 Ubuntu Pro 訂閱',
    subscriptionBody: '修補版本只在 Ubuntu Pro（ESM）提供。升級前請先將主機連結 Ubuntu Pro。',
    wontFixBody:
      '發行版不會在此版本修補這個問題，Smoke 只會放在摘要通知中。若不需要此套件可移除，否則請接受風險。',
    fixedIn: '修補版本',
    severityFromPriority: '嚴重度取自此優先順序',
    trackerNotes: '發行版追蹤系統的說明（第三方內容，Smoke 未審閱）',
    trackerNotesLabel: '來自第三方的發行版追蹤系統說明',
  },
  distroPriority: {
    negligible: '可忽略',
    unimportant: '不重要',
    low: '低',
    medium: '中',
    high: '高',
    critical: '嚴重',
  },
  prioritySource: {
    ubuntu_priority: 'Ubuntu 優先順序',
    debian_urgency: 'Debian 緊急程度',
  },
  distroFixState: {
    fixed: '此版本已修補',
    no_fix: '尚無修補',
    wont_fix: '不修補',
  },
  distroLabel: {
    distro_wont_fix: '發行版不修',
  },
  wontFixReason: {
    ubuntu_priority_negligible: 'Ubuntu 優先順序：negligible',
    debian_urgency_unimportant: 'Debian 緊急程度：unimportant',
    debian_urgency_end_of_life: 'Debian：此套件已停止支援',
    other: '標示為不修',
  },
  priority: {
    P1: '緊急',
    P2: '高',
    P3: '中',
    P4: '低',
  },
  status: {
    open: '未處理',
    in_progress: '處理中',
    pending_verification: '待驗證',
    fixed: '已修補',
    mitigated: '已緩解',
    ignored: '已忽略',
    snoozed: '暫緩中',
    closed: '已關閉',
  },
  fixState: {
    available: '可修補',
    partial: '部分可修補',
    blocked: '修補受阻',
    none: '無修補',
    unknown: '未知',
  },
  slaState: {
    on_track: '進度正常',
    due_soon: '即將到期',
    overdue: '已逾期',
    met: '已達成',
  },
  environment: {
    production: '正式環境',
    staging: '測試環境',
    development: '開發環境',
  },
  role: {
    owner: '擁有者',
    admin: '管理員',
    member: '成員',
  },
  ecosystem: {
    ubuntu: 'Ubuntu',
    debian: 'Debian',
    alpine: 'Alpine',
    npm: 'npm',
    pypi: 'PyPI',
    composer: 'Composer',
  },
  scope: {
    production: '正式相依',
    development: '開發相依',
    optional: '選用相依',
  },
  severity: {
    critical: '嚴重',
    high: '高',
    medium: '中',
    low: '低',
    unknown: '嚴重程度未知',
  },
  priorityRule: {
    kev: '列於 CISA KEV（已知遭利用）',
    critical_epss: '嚴重等級且 EPSS ≥ 10%',
    critical: '嚴重等級',
    high_epss: '高嚴重程度且 EPSS ≥ 5%',
    high_or_medium: '高或中嚴重程度',
    low_or_unknown: '低或未知嚴重程度',
    manual: '由 Admin 手動調成 P1',
  },
  priorityAdjustment: {
    development_scope: '開發用相依套件，降低一級',
    non_production_environment: '測試或開發環境專案，降低一級',
    kev_floor: '已知遭利用：不會低於 P2',
    kernel_not_running: '這個 kernel 沒有在執行：一律列為 P4',
  },
  historyKind: {
    created: '發現',
    status_changed: '狀態變更',
    priority_changed: '優先順序變更',
    assigned: '指派',
    advisories_changed: '漏洞公告變更',
    fix_state_changed: '修補狀態變更',
    mitigations_applied: '套用緩解措施',
    other: '更新',
  },
  statusReason: {
    member_removed: '成員已離開工作區',
    detected: '新的套件清單',
    user: '由使用者',
    user_action: '由使用者',
    new_inventory_clean: '較新的清單已不含此版本',
    reintroduced: '再次出現此版本',
    advisory_withdrawn: '漏洞公告已撤回',
    advisory_no_longer_matches: '漏洞公告不再符合',
    advisory_reinstated: '漏洞公告再次適用',
    new_advisory: '新的漏洞公告',
    snooze_expired: '暫緩期限已到',
    ignore_expired: '忽略期限已到',
    fix_available: '已有修補版本',
    snoozed: '暫緩',
    ignored: '忽略',
    mitigated: '已緩解',
    match: '比對',
    rematch: '重新比對',
    manual_priority: 'Admin 手動調成 P1',
    manual_priority_removed: 'Admin 取消手動 P1',
    superseded: '已被取代',
    group_changed: '漏洞公告群組變更',
    no_asset: '資產已移除',
    direct_all_fixed: '所有漏洞公告都有修補版本',
    direct_some_fixed: '部分漏洞公告有修補版本',
  },
  actorType: {
    user: '使用者',
    system: 'Smoke',
    api_token: 'API 權杖',
    mcp: '程式碼代理（MCP）',
    staff: 'Smoke 人員',
  },
  fixReason: {
    distro_fixed: '發行版已在此版本修補所有漏洞公告。',
    distro_some_fixed: '發行版只在此版本修補部分漏洞公告。',
    distro_no_fix: '發行版尚未為此版本提供修補版本。',
    distro_wont_fix: '發行版不會在此版本修補。',
    direct_all_fixed: '已有版本修補所有漏洞公告。',
    direct_some_fixed: '已發布的版本只修補部分漏洞公告。',
    transitive_in_range: '上層套件已允許修補版本：重新產生 lockfile 即可。',
    transitive_parent_upgrade: '升級上層套件即可引入修補版本。',
    transitive_override: '可用覆寫鎖定修補版本。',
    transitive_unreachable: '已有修補版本，但上層套件不允許，且無法覆寫。',
    no_fixed_version: '目前沒有已發布的版本能修補這些漏洞公告。',
    no_advisories: '沒有有效的漏洞公告。',
    signal_not_configured: '此伺服器未設定修補資料來源。',
    signal_unavailable: '修補資料暫時無法取得。',
    registry_not_found: '套件 registry 中找不到此套件。',
    unknown_group: '修補服務不認得其中一則漏洞公告。',
    invalid_version: '無法解析目前安裝的版本。',
    too_many_advisories: '漏洞公告過多，無法計算共同的修補版本。',
    private_not_sent: '私有套件：依資產設定，未到公開 registry 查詢。',
    available: '已有修補版本。',
    partial: '已有部分修補版本。',
    blocked: '修補受阻。',
    none: '沒有修補版本。',
    unknown: '尚未判定。',
  },
  packageManager: {
    dpkg: 'dpkg（apt）',
    apk: 'apk',
    npm: 'npm',
    pnpm: 'pnpm',
    yarn: 'Yarn',
    yarn_classic: 'Yarn classic',
    yarn_berry: 'Yarn Berry',
    poetry: 'Poetry',
    uv: 'uv',
    pipenv: 'Pipenv',
    pip: 'pip',
    composer: 'Composer',
    unknown: '未知',
  },
  semverDelta: {
    patch: '修補版（patch）',
    minor: '次要版（minor）',
    major: '主要版（major）',
    unknown: '未知',
  },
  strategy: {
    upgrade_os_package: '升級系統套件',
    remove_old_kernel: '移除舊 kernel',
    reboot_new_kernel: '重新開機使用新 kernel',
    rebuild_image: '重新建置映像檔',
    upgrade_direct: '升級',
    upgrade_parent: '升級上層套件',
    override_transitive: '覆寫間接相依版本',
    update_in_range: '在目前範圍內更新',
    mitigate_only: '僅能緩解',
  },
  targetReason: {
    distro_fixed_version: '發行版為此版本提供的修補版本。',
    kernel_not_running: '這個 kernel 已安裝，但沒有在執行。',
    smallest_delta_fixing_all: '能修補所有漏洞公告的最小升級。',
    smallest_delta_fixing_covered: '能修補有修補版本之漏洞公告的最小升級。',
    only_deprecated_candidates: '只有已棄用的版本能修補這些漏洞公告。',
    within_parent_range: '上層套件已允許修補版本。',
    parent_upgrade_small: '小幅升級上層套件即可引入修補。',
    override_smaller_than_parent: '使用覆寫比升級上層套件的變動更小。',
    parent_upgrade_no_override: '此套件管理工具不支援覆寫，因此升級上層套件。',
    no_reachable_fix: '此專案無法取得任何修補版本。',
    no_fixed_version: '目前沒有修補版本。',
    alternative: '其他做法。',
  },
  aiStatus: {
    ready: '已完成',
    queued: '準備中',
    unavailable: '無法使用',
    disabled: '未啟用',
    not_applicable: '不需要',
    not_generated: '尚未產生',
    outdated: '已過期',
  },
  aiStale: {
    fix_changed: '修補資訊在產生後有變更。',
    advisory_changed: '漏洞公告在產生後有變更。',
    stack_changed: '技術堆疊在產生後有變更。',
  },
  aiReason: {
    linux_not_enabled: 'Linux 套件的 AI 分析尚未開啟。',
    fix_state_none: '沒有修補版本可分析，請參考緩解措施。',
    fix_state_unknown: '修補狀態尚未確定。',
    finding_resolved: '此漏洞發現已解決。',
    not_applicable: '此漏洞發現不需要 AI 分析。',
    update_in_range: '在目前版本範圍內重新產生 lockfile 即可修補，不需要分析。',
    in_range_update_only: '在目前版本範圍內重新產生 lockfile 即可修補，不需要分析。',
    container_base_image_unknown:
      'Smoke 無法判斷這個映像檔的基底映像檔。請在資產頁按「編輯」填寫基底映像檔，再產生 AI 分析。',
    kernel_not_running: '這個 kernel 沒有在執行，上方步驟已經完整，不需要 AI 分析。',
    private_package: '私有套件不會送給 AI 分析。',
    no_shareable_path: '相依路徑含有私有套件，無法送出分析。',
    invalid_package: '套件名稱或版本格式無法送出分析。',
  },
  restartLevel: {
    none: '不需要重啟',
    service_restart: '重啟服務',
    reboot: '重新開機',
    unknown: '無法判斷',
  },
  riskLevel: {
    low: '低',
    medium: '中',
    high: '高',
    unknown: '未知',
  },
  ratingReason: {
    wrong_target: '目標版本錯誤',
    missed_breaking_change: '遺漏了破壞性變更',
    steps_did_not_work: '步驟無法執行',
    unclear: '說明不清楚',
    not_applicable: '不適用於我們',
    other: '其他',
  },
  mitigationKind: {
    disable_feature: '停用功能',
    config_change: '調整設定',
    restrict_input: '限制輸入',
    restrict_network_access: '限制網路存取',
    isolate_host: '隔離主機',
    stop_service: '停止服務',
    waf_rule: 'WAF／反向代理',
    least_privilege: '最小權限',
    remove_component: '移除元件',
    monitor_detect: '監控與偵測',
    replace_package: '更換套件',
    other: '其他',
  },
  mitigationRef: {
    generic: '通用緩解措施',
    ai: 'AI 建議',
    sourced: '來自公開來源的緩解措施',
    other: '其他緩解措施',
  },
  effectiveness: {
    blocks_exploit: '可阻擋攻擊',
    reduces_risk: '可降低風險',
    detection_only: '僅能偵測',
  },
  impact: {
    low: '低',
    medium: '中',
    high: '高',
    service_outage: '服務中斷',
  },
  ignoreReason: {
    not_affected: '不受影響',
    accepted_risk: '接受風險',
    false_positive: '誤判',
  },
  ignoreReasonHelp: {
    not_affected: '有漏洞的程式碼未被使用或無法觸及。',
    accepted_risk: '我們暫時接受此風險。符合「緩解或接受風險」的 SLA。',
    false_positive: '比對結果錯誤，也建議一併回報。',
  },
  advisoryState: {
    active: '有效',
    withdrawn: '已撤回',
    not_matching: '不再符合',
  },
  referenceType: {
    ADVISORY: '公告',
    ARTICLE: '文章',
    DETECTION: '偵測',
    DISCUSSION: '討論',
    EVIDENCE: '證據',
    FIX: '修補',
    INTRODUCED: '引入',
    PACKAGE: '套件',
    REPORT: '回報',
    WEB: '網頁',
  },
  matchStatus: {
    pending: '比對中…',
    matched: '已比對',
    failed: '失敗',
  },
  format: {
    'smoke-host': '主機指令輸出',
    'npm-package-lock': 'npm lockfile',
    'pnpm-lock': 'pnpm lockfile',
    'yarn-classic': 'Yarn classic lockfile',
    'yarn-berry': 'Yarn Berry lockfile',
    'poetry-lock': 'Poetry lockfile',
    'uv-lock': 'uv lockfile',
    'pipfile-lock': 'Pipfile.lock',
    'requirements-txt': 'requirements.txt',
    'composer-lock': 'Composer lockfile',
    'package-json': 'package.json',
    'cyclonedx-json': 'CycloneDX SBOM',
    'spdx-json': 'SPDX SBOM',
  },
  parseNote: {
    os_inventory:
      '{{distro}} {{release}}：{{packages}} 個已安裝套件，來自 {{sourcePackages}} 個原始碼套件。',
    os_packages_not_read: '未讀取此 SBOM 中的系統套件：{{reason}}應用程式套件已讀取。',
    scope_unknown: '此檔案未標示哪些套件屬於開發用，全部視為正式相依。',
    direct_inferred: '此檔案未列出專案本身的相依套件；沒有被其他套件依賴的套件會視為直接相依。',
    no_dependency_graph: '此檔案沒有相依關係圖，因此無法提供相依路徑。',
    unreachable_packages: '{{count}} 個套件無法從專案的相依套件追溯到，因此沒有相依路徑。',
    companion_manifest: '已與旁邊的 lockfile 一併讀取。',
    scope_from_file_name: '範圍是依檔名判斷（例如 requirements-dev.txt）。',
  },
  parseError: {
    host_asset_required: '指令輸出只能加到 Linux 主機或容器映像檔資產，請在專案頁建立。',
    os_inventory_required:
      '主機和容器映像檔需要指令產生的檔案或 SBOM。lockfile 請上傳到應用程式資產，否則會取代這台機器的系統套件清單。',
    multiple_os_inventories:
      '一次上傳只能包含一台主機或一個映像檔的系統套件，請把每台主機上傳到各自的資產。',
    unsupported_os: '只支援 Ubuntu、Debian 和 Alpine。',
    malformed_host_inventory: '無法讀取指令輸出。請重新執行指令，並貼上它印出的全部內容。',
    unsupported_file: '不是支援的 manifest 或 lockfile。',
    unsupported_version:
      '不支援此 lockfile 版本（例如 npm lockfile v1）。請用新版套件管理工具重新產生。',
    malformed_json: '不是有效的 JSON。',
    malformed_yaml: '不是有效的 YAML。',
    malformed_toml: '不是有效的 TOML。',
    malformed_lockfile: '無法讀取此 lockfile。',
    invalid_encoding: '不是 UTF-8 文字。',
    invalid_path: '路徑無效。',
    duplicate_path: '有兩個檔案路徑相同。',
    file_too_large: '超過 {{maxMb}} MB。',
    too_many_files: '超過 {{maxFiles}} 個檔案。',
    total_too_large: '檔案總大小超過 {{maxMb}} MB。',
  },
  skipReason: {
    workspace: 'Workspace 套件（屬於此儲存庫）',
    local: '本機路徑',
    git: '從 Git 安裝',
    link: '連結的套件',
    non_registry: '不是來自 registry（網址或路徑）',
    alias_unresolved: '無法解析別名',
    malformed: '格式錯誤的項目',
    invalid_name: '套件名稱無效',
    unsupported_ecosystem: '不支援的生態系',
    extraneous: '多餘的套件（專案未要求）',
  },
  unresolvedReason: {
    not_pinned:
      '未以 == 鎖定版本。請鎖定版本，或上傳 lockfile（poetry.lock、uv.lock、Pipfile.lock）或 pip freeze 的輸出。',
    include: '引用了另一個 requirements 檔案，請一併上傳。',
    unpinned_manifest: '這是 manifest 中的版本範圍，不是實際安裝的版本，請上傳 lockfile。',
    no_purl: 'SBOM 項目缺少 package URL。',
    no_version: '沒有提供版本。',
    unparseable: '無法讀取此行。',
  },
  snapshotSource: {
    upload: '上傳',
    mcp: '程式碼代理（MCP）',
    collector: 'Linux 收集程式',
    ci: 'CI',
    ci_gitlab: 'GitLab CI',
    ci_github: 'GitHub Actions',
    repository: 'GitHub',
    api: 'API',
    github: 'GitHub',
  },
  assetKind: {
    manifest_upload: '上傳',
    repository: '程式碼儲存庫',
    host: '伺服器',
    container_image: '容器映像檔',
    database: '資料庫',
  },
  eolStatus: {
    eol: '已停止支援',
    eol_soon: '即將停止支援',
    supported: '支援中',
    not_found: '未知版本',
    not_tracked: '未追蹤',
    unavailable: '無法取得',
  },
  stackComponent: {
    runtime: '執行環境',
    framework: '框架',
    os: '作業系統',
    database: '資料庫',
    service: '服務',
  },
  stackSource: {
    detected: '自動偵測',
    asset: '手動',
    project: '專案層級',
  },
  tokenKind: {
    personal: '個人',
    project: '專案',
    asset: '資產',
    oauth: 'OAuth（程式碼代理）',
  },
  tokenScope: {
    'findings:read': '讀取漏洞發現',
    'findings:write': '更新漏洞發現',
    'inventory:write': '提交套件清單',
  },
  auditAction: {
    'client.created': '建立客戶',
    'client.archived': '封存客戶',
    'client.unarchived': '取消封存客戶',
    'client.deleted': '刪除客戶',
    'project.created': '建立專案',
    'project.archived': '封存專案',
    'project.unarchived': '取消封存專案',
    'project.deleted': '刪除專案',
    'asset.created': '建立資產',
    'asset.updated': '更新資產',
    'asset.private_registry_lookup_changed': '變更私有套件查詢設定',
    'asset.archived': '封存資產',
    'asset.unarchived': '取消封存資產',
    'asset.deleted': '刪除資產',
    'assignment.added': '指派成員',
    'assignment.removed': '移除指派',
    'api_token.created': '建立 API 權杖',
    'api_token.revoked': '撤銷 API 權杖',
    'api_token.renamed': 'API 權杖改名',
    'finding.false_positive_reported': '回報誤判',
    'finding.fix_prompt_copied': '複製修復提示',
    'finding.remediation_requested': '要求產生 AI 分析',
    'project.eol_prompt_generated': '產生升級提示詞',
    'finding.mitigations_applied': '套用緩解措施',
    'finding.remediation_rated': '評分修復建議',
    'inventory.snapshot_created': '上傳套件清單',
    'invitation.created': '邀請成員',
    'invitation.revoked': '撤銷邀請',
    'mcp.tool_called': '呼叫 MCP 工具',
    'member.joined': '成員加入',
    'member.removed': '移除成員',
    'member.role_changed': '變更角色',
    'oauth.authorized': '授權應用程式',
    'oauth.refresh_token_reused': '偵測到 refresh token 重複使用',
    'workspace.created': '建立工作區',
    'workspace.updated': '更新工作區',
    'workspace.deletion_requested': '申請刪除工作區',
    'workspace.sla_policy_changed': '變更 SLA 政策',
    'asset.addresses_updated': '更新伺服器 IP 位址',
    'asset.stack_updated': '更新資產的技術堆疊',
    'asset.deployment_set': '設定資產部署的伺服器',
    'asset.deployment_removed': '移除資產部署的伺服器',
    'advisory.manual_p1_set': '手動調成 P1',
    'advisory.manual_p1_removed': '取消手動 P1',
  },
  auditTarget: {
    workspace: '工作區',
    invitation: '邀請',
    membership: '成員',
    api_token: 'API 權杖',
    finding: '漏洞發現',
    asset: '資產',
    snapshot: '快照',
    client: '客戶',
    project: '專案',
  },
  loginMethod: {
    password: '電子郵件與密碼',
    google: 'Google',
    github: 'GitHub',
  },
  channel: {
    email: 'Email',
    line: 'LINE',
    telegram: 'Telegram',
  },
  timing: {
    immediate: '立即',
    daily: '每日摘要',
    weekly: '每週摘要',
    off: '關閉',
  },
  notificationCategory: {
    finding_alert: '新漏洞與優先順序提高',
    sla_reminder: 'SLA 提醒',
    fix_available: '已有修補版本',
    mitigation: '新的緩解措施',
    mitigation_review: '緩解措施複查',
    eol: '停止支援通知',
    collector_stale: '收集程式沒有回報',
    upload_problem: '自動上傳即將中斷',
    digest: '摘要',
  },
  notificationCategoryHelp: {
    sla_reminder: '「2 天內到期」與「已逾期」提醒，寄給被指派者與管理員。',
    fix_available: '原本沒有可用修補的漏洞出現修補版本。',
    mitigation: '沒有修補版本的漏洞出現有來源依據的緩解措施。',
    mitigation_review: '已緩解的漏洞到了複查日期。',
    eol: '專案使用的執行環境、框架或作業系統即將或已經停止支援。',
    collector_stale:
      '主機上的 Linux 收集程式超過 3 天沒有回報（只通知管理員，恢復回報前只通知一次）。',
    upload_problem:
      'CI 或收集程式正在使用的上傳權杖 30 天內到期或已到期，或追蹤的 GitHub 儲存庫開始掃描失敗（只通知管理員）。',
  },
  emailList: {
    finding_alert: '新漏洞與優先順序提高',
    sla_reminder: 'SLA 提醒',
    fix_available: '已有修補版本',
    mitigation: '新的緩解措施',
    mitigation_review: '緩解措施複查',
    collector_stale: '收集程式沒有回報',
    upload_problem: '自動上傳即將中斷',
    digest: '每日與每週摘要',
  },
  deliveryStatus: {
    queued: '排隊中',
    sent: '已傳送',
    failed: '失敗',
    skipped: '略過',
  },
  deliveryMode: {
    immediate: '立即',
    daily: '每日摘要',
    weekly: '每週摘要',
  },
  deliveryError: {
    channel_unavailable: '聊天帳號已不再連結',
    opted_out: '已取消訂閱',
    no_longer_applicable: '已不適用',
    blocked: '收件人已封鎖（已解除連結）',
    rate_limited: '傳送頻率受限',
    invalid_request: '聊天服務拒絕',
    not_configured: '管道尚未設定',
    migrated: '群組已移轉',
    transient: '暫時性錯誤',
  },
  unlinkReason: {
    user: '已解除連結',
    admin: '由管理員解除連結',
    blocked: '機器人已被移出群組',
    relinked: '已由新的連結取代',
    unlinked: '已解除連結',
  },
  weekday: {
    monday: '星期一',
  },
  planTier: {
    free: '免費',
    pro: 'Pro',
    agency: 'Agency',
  },
  planLimit: {
    title: '已達方案上限',
    projects:
      '{{plan}}方案最多 {{limit}} 個啟用中的專案，目前已使用 {{used}} 個。請封存專案，或升級方案以新增更多。',
    assets:
      '{{plan}}方案最多 {{limit}} 個啟用中的資產，目前已使用 {{used}} 個。請封存資產，或升級方案以新增更多。',
    workspaces:
      '{{plan}}方案只能擔任 {{limit}} 個工作區的帳務擁有者，算上這個會是 {{used}} 個。請升級方案，或刪除不用的工作區。',
    featureUnavailable: '{{plan}}方案不含{{feature}}，升級方案後即可使用。',
    feature: {
      pdfReports: 'PDF 客戶報告',
    },
    seePlans: '查看方案並升級',
    chatChannels: '{{plan}}方案只以 Email 寄送通知，升級方案後即可使用 Telegram 通知與群組。',
  },
  pricing: {
    title: '方案與價格',
    description: '價格來自付款平台的商品目錄。即使達到上限，既有專案的比對與 P1 通知也不會停止。',
    publicDescription: '免費開始使用，追蹤的客戶專案變多時再升級。價格以新台幣計。',
    accountDescription:
      '方案屬於你的帳號，涵蓋你擔任帳務擁有者的所有工作區，上限由這些工作區合計。免費方案只能擁有一個工作區。',
    paidByYou: '這個工作區由你的帳號方案支付。變更方案會套用到你擔任帳務擁有者的所有工作區。',
    paidBy: '這個工作區的方案由 {{name}} 的帳號支付。要變更方案，請聯絡 {{name}}。',
    noBillingOwner: '這個工作區目前沒有帳務擁有者，適用免費方案的限制。',
    manage: '管理我的方案',
    monthly: '月繳',
    perMonth: '／月',
    refundNote:
      '付費方案按月計費並自動續約，付款後立即生效，已付費用不予退款。可隨時取消，當期結束前仍可使用。詳見<refund>退款政策</refund>。',
    free: '不收費',
    priceUnavailable: '目前無法取得價格',
    currentPlan: '目前方案',
    scheduled: '週期結束時生效',
    unlimited: '不限',
    workspaces: '{{value}} 個工作區',
    workspacesShared: '不限工作區數量，以下上限由所有工作區合計',
    projects: '{{value}} 個專案',
    assets: '{{value}} 個資產',
    aiCalls: '每月 {{value}} 次 AI 修復分析',
    aiFairUse: 'AI 修復分析（合理使用）',
    notificationsFree: 'Email、每週摘要（P1 立即通知）',
    notificationsAll: 'Email 與 Telegram，可自訂時機',
    pdfReports: 'PDF 客戶報告',
    csvExport: 'CSV 匯出',
    notIncluded: '不含：',
    getStarted: '開始使用',
    action: {
      purchase: '選擇 {{plan}}',
      upgrade: '升級至 {{plan}}',
      downgrade: '改為 {{plan}}',
    },
  },
  billing: {
    title: '帳務',
    description:
      '你的帳號的方案、付款方式、發票資料與付款紀錄。方案涵蓋你擔任帳務擁有者的所有工作區。',
    seePlans: '查看方案',
    changePlan: '變更方案',
    unconfigured: '此 Smoke 伺服器尚未設定線上付款。',
    operationPending: '有一筆付款正在確認中，確認完成後才能再次變更方案。',
    operationPendingKind: '有一筆付款正在確認中（{{kind}}），此頁面會自動更新。',
    override: 'Smoke 工作人員已將你的帳號設為 {{plan}}。',
    overrideUntil: 'Smoke 工作人員已將你的帳號設為 {{plan}}，至 {{date}} 為止。',
    workspaces: {
      title: '此方案涵蓋的工作區',
      none: '你目前不是任何工作區的帳務擁有者。',
    },
    downgradeScheduledTitle: '已排定方案變更',
    downgradeScheduledBody:
      '方案將於 {{date}}（已付費週期結束時）改為 {{plan}}，在此之前維持目前方案。',
    downgradeScheduledShort: '已排定於週期結束時改為 {{plan}}。',
    downgradeScheduledToast: '已排定於週期結束時改為 {{plan}}。',
    downgradeCanceled: '已保留目前方案。',
    keepPlan: '保留 {{plan}}',
    dunning: {
      title: '上一次付款失敗',
      body: '我們將於 {{next}} 再次扣款（第 {{attempt}} 次）。請在 {{grace}} 前更新信用卡以保留方案，之後工作區會改用免費版的上限。既有專案仍會持續比對。',
    },
    plan: {
      title: '目前方案',
      subscribed: '付費訂閱：{{plan}}',
      interval: '計費方式',
      period: '目前週期',
      renews: '續訂日',
      ends: '付費至',
      credit: '抵用金',
    },
    usage: {
      title: '用量',
      projects: '專案',
      assets: '資產',
      aiCalls: '本月 AI 分析',
      workspaces: '工作區',
      shared: '你擔任帳務擁有者的所有工作區合計。',
      of: '{{used}}／{{limit}}',
      fairUse: '合理使用',
    },
    card: {
      title: '付款方式',
      summary: '{{brand}} 末四碼 {{last4}}',
      card: '信用卡',
      expires: '有效期限 {{date}}',
      none: '尚未儲存信用卡。自動續訂需要已儲存的信用卡。',
      add: '新增信用卡',
      replace: '更換信用卡',
      note: '卡號在付款頁面輸入，不會經過 Smoke。',
    },
    details: {
      title: '帳務資料與電子發票',
      description: '用於收據，以及由 Aircraft Pay 開立的台灣電子發票。',
      email: '帳務 Email',
      emailHint: '收據、發票與付款提醒會寄到這裡。',
      name: '帳務名稱',
      nameHint: '選填：付款頁面上顯示的個人或公司名稱。',
      save: '儲存帳務資料',
    },
    recipient: {
      title: '發票載具',
      hint: '電子發票的交付方式。發票由 Aircraft Pay（飛空艇科技股份有限公司）開立，Smoke 只負責傳遞這些資料。',
      type: {
        member: '會員載具',
        mobile: '手機條碼',
        citizen: '自然人憑證',
        donation: '捐贈發票',
        b2b: '公司（統一編號）',
      },
      typeHint: {
        member: '存放於您的 Aircraft Pay 帳號',
        mobile: '例如 /ABC1234',
        citizen: '2 個英文字母加 14 位數字',
        donation: '受贈單位的愛心碼',
        b2b: '統一編號與公司名稱',
      },
      mobile: '手機條碼',
      mobileHint: '「/」加上 7 個字元：數字、大寫英文字母、「.」、「+」或「-」。',
      citizen: '自然人憑證號碼',
      citizenHint: '2 個大寫英文字母加 14 位數字。',
      donation: '愛心碼',
      donationHint: '3 到 7 位數字。',
      taxId: '統一編號',
      taxIdHint: '8 位數字。',
      companyName: '公司名稱',
      errors: {
        required: '必填',
        mobileFormat: '請輸入「/」加上 7 個字元（0–9、A–Z、「.」、「+」、「-」）。',
        citizenFormat: '請輸入 2 個大寫英文字母加 14 位數字。',
        donationFormat: '請輸入 3 到 7 位數字。',
        taxIdFormat: '請輸入 8 位數字。',
        taxIdChecksum: '統一編號不正確，請檢查數字。',
        companyNameLength: '最多 60 個字。',
      },
    },
    history: {
      title: '付款紀錄',
      empty: '尚無付款紀錄',
      date: '日期',
      item: '項目',
      amount: '金額',
      status: '狀態',
      invoice: '發票號碼',
      mock: '模擬',
      noInvoiceMock: '無發票（模擬付款）',
      invoicePending: '開立中',
      refunded: '已退款 {{amount}}',
    },
    change: {
      purchaseTitle: '選擇 {{plan}}',
      upgradeTitle: '升級至 {{plan}}',
      downgradeTitle: '改為 {{plan}}',
      purchaseBody:
        '{{plan}} 費用為 {{price}} {{interval}}，付款確認後立即生效，之後每月自動續約，直到你取消。',
      consent:
        '我已閱讀並同意<terms>服務條款</terms>和<refund>退款政策</refund>，同意付款後立即開始提供服務，並了解已付費用不予退款。',
      creditApplies: '將優先折抵您的抵用金 {{amount}}。',
      checkoutNote: '您會在安全的付款頁面輸入信用卡，卡片會儲存供續訂使用。',
      chargedToCard: '金額將從已儲存的信用卡扣款。',
      upgradeSamePeriod:
        '您只需支付目前週期剩餘時間的按比例差額。{{plan}} 立即生效，週期仍於 {{end}} 結束。',
      quoteCaption: '升級應付金額',
      downgradeBody: '方案將於 {{date}}（已付費週期結束時）改為 {{plan}}。剩餘期間不退款。',
      downgradeBodyNoDate: '方案將於已付費週期結束時改為 {{plan}}。',
      confirmPay: '繼續：支付 {{amount}}',
      confirmDowngrade: '排定變更',
    },
    quote: {
      proratedDifference: '按比例差額',
      creditApplied: '抵用金',
      dueNow: '本次應付',
    },
    return: {
      confirmingTitle: '正在確認付款…',
      confirmingBody: '正在等待付款服務確認，確認後方案才會變更；此頁面會自動更新。',
      slowTitle: '仍在等待確認',
      slowBody:
        '付款服務尚未確認。您不需要再次付款：確認一到方案就會變更，我們也會寄信通知擁有者。',
      doneTitle: '帳務已更新',
      planNow: '目前方案：{{plan}}。',
      cardSaved: '已儲存付款方式。',
      canceledTitle: '已取消付款',
      canceledBody: '沒有任何扣款，方案維持不變。',
      expiredBody: '付款頁面在完成付款前已逾時，沒有任何扣款。',
      declinedTitle: '付款未成功',
      declinedBody: '這筆付款沒有成功，沒有任何扣款，方案維持不變。',
      cardDeclinedBody: '無法儲存這張信用卡，沒有任何扣款。',
      reasonLabel: '原因',
      reason: {
        card_declined: '發卡銀行拒絕了這筆交易。',
        insufficient_funds: '額度不足。',
        expired_card: '信用卡已過期。',
        incorrect_cvc: '安全碼不正確。',
        processing_error: '付款服務無法處理這張信用卡。',
        payment_failed: '付款失敗。',
        payment_method_required: '沒有可用的已儲存信用卡。',
        checkout_expired: '付款頁面已逾時。',
      },
      tryAgain: '重新選擇方案',
      notFound: '找不到這筆訂單。',
    },
    status: {
      active: '正常',
      past_due: '付款逾期',
    },
    orderKind: {
      purchase: '購買',
      renewal: '續訂',
      upgrade: '升級',
      card_setup: '設定信用卡',
    },
    itemType: {
      plan: '方案',
    },
    paymentStatus: {
      succeeded: '已付款',
      pending: '處理中',
      processing: '處理中',
      failed: '失敗',
      refunded: '已退款',
      partially_refunded: '部分退款',
      canceled: '已取消',
    },
    failure: {
      PAYMENT_FAILED: '信用卡遭拒',
      PAYMENT_METHOD_REQUIRED: '沒有已儲存的信用卡',
      GATEWAY_ERROR: '結果不明',
      MAINTENANCE: '付款服務維護中',
      MERCHANT_NOT_ROUTED: '付款路由問題',
    },
  },
  reports: {
    title: '報告與匯出',
    description:
      '客戶報告（PDF）與 CSV 匯出會在背景產生。下載連結有時效，重新開啟列表即可取得新連結。',
    adminOnly: '只有擁有者與管理員可以建立報告與匯出。',
    clientTitle: '客戶報告（PDF）',
    clientDescription: '單一客戶在指定期間內的優先順序摘要、SLA 達成率、停止支援通知與已修補項目。',
    client: '客戶',
    noClient: '未指定客戶的專案',
    from: '開始日期',
    to: '結束日期',
    language: '報告語言',
    timeZoneHint: '日期為工作區時區（{{timeZone}}）的日曆日。',
    generate: '產生報告',
    periodError: {
      required: '請選擇開始與結束日期。',
      order: '結束日期早於開始日期。',
      tooLong: '報告期間最多 366 天。',
    },
    exportTitle: 'CSV 匯出',
    exportDescription: '將漏洞發現或處理決策（含 VEX 狀態）匯出為 UTF-8 試算表檔案。',
    exportKind: '匯出內容',
    scope: '範圍',
    allProjects: '所有專案',
    clients: '客戶',
    projects: '專案',
    exportAudited: '每次匯出都會記錄在稽核記錄中。',
    export: '建立 CSV',
    queued: '已排入佇列，檔案完成後會出現在列表中。',
    listTitle: '最近的報告與匯出',
    emptyTitle: '尚無報告',
    emptyBody: '請在上方產生客戶報告或 CSV 匯出。',
    columns: {
      kind: '類型',
      scope: '客戶或範圍',
      status: '狀態',
      created: '建立時間',
    },
    kind: {
      client_pdf: '客戶報告（PDF）',
      findings_csv: '漏洞發現（CSV）',
      decisions_csv: '處理決策（CSV）',
    },
    status: {
      queued: '排隊中',
      running: '產生中',
      ready: '已完成',
      failed: '失敗',
      expired: '已過期',
    },
    error: {
      render_failed: '無法產生檔案。',
      CLIENT_NOT_FOUND: '客戶已不存在。',
    },
    rows_one: '{{count}} 列',
    rows_other: '{{count}} 列',
    working: '處理中…',
    download: '下載',
    availableUntil: '保存至 {{date}}',
  },
  github: {
    title: 'GitHub',
    description:
      '透過 Smoke GitHub App（僅能讀取內容與中繼資料）連接儲存庫。推送時、每天一次，或您要求時，Smoke 會掃描選定的清單檔。',
    adminOnly: '只有擁有者與管理員可以管理 GitHub 連線。',
    connect: '連接 GitHub',
    connectAnother: '連接其他帳號',
    notConfiguredTitle: '此 Smoke 伺服器尚未設定 GitHub',
    notConfiguredBody:
      '此伺服器尚未設定 Smoke GitHub App 的憑證，因此無法連接儲存庫。您仍可以手動將鎖定檔與清單檔上傳到資產。',
    uploadInstead: '改為上傳清單檔',
    noInstallationsTitle: '尚未連接 GitHub 帳號',
    noInstallationsBody: '請在您的帳號或組織安裝 Smoke GitHub App，並選擇它可以讀取的儲存庫。',
    installations: '已連接的帳號',
    suspended: '已停用',
    addRepository: '新增儲存庫',
    repositories: '追蹤中的儲存庫',
    noRepositoriesTitle: '尚未追蹤任何儲存庫',
    noRepositoriesBody: '將儲存庫加入專案，再選擇分支與清單檔。',
    setupFailed: '無法連接 GitHub',
    requested: '已送出安裝申請，需由 GitHub 組織擁有者核准。',
    connected: '已連接 GitHub，請新增要追蹤的儲存庫。',
    branch: '分支',
    defaultBranch: '預設分支：{{branch}}',
    manifestCount_one: '{{count}} 個清單檔',
    manifestCount_other: '{{count}} 個清單檔',
    accessLost: '已失去存取權',
    configure: '分支與清單檔',
    rescan: '立即重新掃描',
    scanQueued: '已排入掃描。',
    untrack: '停止追蹤',
    confirmUntrack: '要停止追蹤 {{repo}} 嗎？資產與快照會保留。',
    lastScan: '上次掃描',
    neverScanned: '尚未掃描',
    chooseManifests: '首次掃描前，請先選擇要追蹤的清單檔。',
    addTitle: '從 {{account}} 新增儲存庫',
    project: '專案',
    projectHint: '儲存庫會成為此專案的資產（計入方案用量）。',
    filter: '篩選儲存庫',
    noAvailable: '沒有符合的儲存庫。',
    private: '私有',
    alreadyTracked: '已追蹤',
    track: '追蹤儲存庫',
    tracked:
      '已新增儲存庫，Smoke 會掃描預設分支上找到的清單檔。要換分支或清單檔，按「分支與清單檔」。',
    configureTitle: '{{repo}} 的分支與清單檔',
    discover: '尋找清單檔',
    manifests: '要掃描的清單檔',
    manifestsHint: '分支上支援的鎖定檔與清單檔（略過 vendored 目錄）。',
    noManifests: '尚未找到支援的清單檔。請選擇分支，再按「尋找清單檔」。',
    truncated: '儲存庫較大，只搜尋了一部分。',
    accountType: {
      User: '個人帳號',
      Organization: '組織',
    },
    selection: {
      all: '所有儲存庫',
      selected: '選定的儲存庫',
    },
    setupError: {
      state_invalid: '設定連結已過期或屬於其他使用者，請重新開始。',
      forbidden: '只有擁有者與管理員可以連接 GitHub。',
      installation_invalid: 'GitHub 未回傳有效的安裝，請重新開始。',
      installation_not_accessible: '您的 GitHub 使用者無法存取此安裝。',
      github_unavailable: '無法連線到 GitHub，請稍後再試。',
    },
    scanStatus: {
      ok: '已掃描',
      unchanged: '沒有變更',
      skipped: '已掃描過',
      failed: '失敗',
    },
    scanError: {
      installation_unavailable: 'GitHub App 已無法讀取此儲存庫。',
      no_manifests: '分支上沒有任何選定的清單檔。',
      branch_not_found: '分支不存在。',
      file_too_large: '清單檔過大。',
      github_error: 'GitHub 回傳錯誤。',
      inventory_lockfile_required:
        '只找到 package.json：請把 lockfile（package-lock.json、pnpm-lock.yaml 或 yarn.lock）加入儲存庫。',
      inventory_parse_failed: '清單檔無法解析。',
    },
  },
  staff: {
    badge: '工作人員',
    nav: '管理後台',
    backToApp: '回到 Smoke',
    notStaff: '此區域僅限 Smoke 工作人員使用。',
    twoFactorTitle: '需要兩步驟驗證',
    twoFactorBody: 'Smoke 工作人員必須使用兩步驟驗證，請先開啟後再回來。',
    twoFactorLink: '開啟帳號設定',
    adminOnly: '此處的變更需要管理員工作人員角色。',
    reason: '原因',
    reasonHint: '至少 {{min}} 個字，會記錄在稽核記錄中。',
    reasonAudited: '原因會記錄在該工作區的稽核記錄中。',
    role: {
      support: '客服',
      admin: '管理員工作人員',
    },
    tabs: {
      health: '同步狀態',
      falsePositives: '誤判回報',
      mitigationReviews: '緩解措施審核',
      aiUsage: 'AI 用量',
      workspaces: '工作區',
      flags: '功能旗標',
      alerts: '警示',
      users: '工作人員',
      feedback: '意見回饋',
    },
    health: {
      title: '漏洞資料來源同步狀態',
      description: '資料來自 smoke-signal。延遲為距離上次成功同步的時間。',
      unavailable: '無法連線到 smoke-signal。',
      sources: '資料來源',
      dataVersion: '資料版本 {{version}}',
      source: '來源',
      lastSuccess: '上次成功',
      lag: '延遲',
      records: '筆數（上次執行）',
      quarantined: '隔離',
      state: '狀態',
      ok: '正常',
      disabled: '已停用',
      failing_one: '連續失敗 {{count}} 次',
      failing_other: '連續失敗 {{count}} 次',
      dropped_one: '捨棄 {{count}} 個部分',
      dropped_other: '捨棄 {{count}} 個部分',
      minutes_one: '{{count}} 分鐘',
      minutes_other: '{{count}} 分鐘',
      hours_one: '{{count}} 小時',
      hours_other: '{{count}} 小時',
    },
    fp: {
      title: '誤判回報',
      description: '客戶送出的回報。接受後，會為相關漏洞群組記錄此決定。',
      state: '審查狀態',
      states: {
        pending: '待審查',
        accepted: '已接受',
        rejected: '已駁回',
      },
      empty: '此佇列沒有項目',
      package: '套件',
      advisories: '漏洞群組',
      note: '客戶備註',
      reported: '回報時間',
      accept: '接受',
      reject: '駁回',
      acceptTitle: '接受 {{package}} 的回報',
      rejectTitle: '駁回 {{package}} 的回報',
      reasonAudited: '決定與原因會記錄在客戶工作區的稽核記錄中。',
      done: '已記錄決定。',
    },
    mr: {
      title: '附來源緩解措施審核',
      description:
        '列在 KEV 或嚴重度為 critical 的漏洞公告，由 AI 整理自公開來源的結果。這些結果會立即顯示給客戶，並標示尚未經人工審核；請核准，或隱藏有誤的項目。',
      unavailable: '無法連線到 smoke-signal，暫時無法顯示審核佇列。',
      empty: '目前沒有待審核的項目',
      subject: '漏洞公告群組',
      language: '語言',
      severity: '嚴重度',
      items: '項目數',
      generated: '產生時間',
      state: '狀態',
      states: { unreviewed: '尚未審核', approved: '已核准' },
      open: '審核',
      back: '回到審核佇列',
      superseded: '已有較新的結果取代這一筆，無法再做審核決定。',
      fixStatus: '來源中的修補狀態：{{status}}',
      officialSupport: '有官方來源說明',
      summary: '摘要',
      conflicts: '來源說法不一致',
      sources: '來源',
      license: '授權：{{license}}',
      hidden: '已隱藏',
      hide: '對客戶隱藏',
      approve: '核准',
      approveTitle: '核准這筆結果',
      approveHint_zero: '所有項目都維持顯示。',
      approveHint_one: '同時隱藏已選取的 {{count}} 個項目。',
      approveHint_other: '同時隱藏已選取的 {{count}} 個項目。',
      hideSelected_one: '隱藏 {{count}} 個項目',
      hideSelected_other: '隱藏 {{count}} 個項目',
      hideTitle: '隱藏已選取的項目',
      hideHint: '隱藏的項目不會再顯示給客戶，而且無法復原。',
      reasonAudited: '審核決定、審核人和理由會記錄在 smoke-signal 與員工操作紀錄中。',
      done: '已記錄審核決定。',
      untrustedNote: '引用原文和來源標題是第三方內容，以純文字顯示。',
    },
    ai: {
      title: 'BlueMagpie 用量與成本',
      description: '只計算實際呼叫（快取命中另列）。成本依 token 單價估算。',
      calls: '呼叫次數',
      cacheHits: '快取命中',
      failures: '失敗',
      tokens: 'Token 數',
      tokensInOut: 'Token 輸入／輸出',
      cost: '成本（美元）',
      fairUse: '合理使用 {{value}}',
      empty: '此期間沒有用量',
    },
    ws: {
      title: '工作區',
      description: '只顯示方案與規模。需填寫原因開啟唯讀存取，才能看到更多。',
      search: '依名稱或帳務擁有者的 Email 搜尋',
      empty: '找不到工作區',
      name: '工作區',
      plan: '方案',
      billingOwner: '帳務擁有者',
      status: '狀態',
      periodEnd: '週期結束',
      counts: '成員 · 專案 · 資產',
      countsValue: '{{members}} · {{projects}} · {{assets}}',
      deleting: '已申請刪除',
      overridden: '手動指定',
      pending: '將改為 {{plan}}',
      detailTitle: '工作區',
      overview: '概況',
      timeZone: '時區',
      created: '建立日期',
      projects: '專案',
      openFindings: '未處理漏洞',
      override: '手動指定',
      members: '成員',
    },
    access: {
      title: '唯讀存取',
      description:
        '必須取得存取授權，才能查看詳細資料、付款與工作區本身。授權與原因會顯示在該工作區的稽核記錄中。',
      open: '開啟唯讀存取',
      confirm: '開啟存取',
      reasonAudited: '客戶會在稽核記錄中看到此原因。',
      minutes: '時長（分鐘）',
      minutesHint: '5 到 1,440 分鐘，預設 60。',
      activeTitle: '工作人員存取：唯讀',
      activeBody: '您正以 Smoke 工作人員身分檢視此工作區，至 {{until}} 為止，無法進行任何變更。',
      reasonLabel: '原因',
      openWorkspace: '開啟工作區',
      backToConsole: '回到管理後台',
      end: '結束存取',
    },
    override: {
      title: '手動指定方案',
      description:
        '手動指定方案（試用、特殊合約），每次變更都會記錄稽核。套用到帳務擁有者的帳號，也就是他擔任帳務擁有者的所有工作區。',
      plan: '方案',
      until: '到期日',
      untilHint: '選填，留空表示沒有到期日。',
      projects: '專案上限',
      assets: '資產上限',
      limitHint: '選填，留空沿用方案上限。',
      apply: '套用指定方案',
      remove: '移除指定方案',
    },
    credit: {
      title: '抵用金',
      description: '用於折抵之後的付款（新台幣）。輸入負數可扣除抵用金。',
      amount: '金額（新台幣）',
      amountHint: '整數，例如 500 或 -500。',
      apply: '調整抵用金',
      confirmTitle: '調整抵用金 {{amount}}',
      done: '抵用金餘額為 {{balance}}。',
    },
    payments: {
      title: '付款與退款',
      id: '付款',
      refunds: '退款',
      full: '全額',
      refund: '退款',
      refundTitle: '退款：付款 {{id}}',
      refundNote: '僅限信用卡付款。處理中的退款不會重複送出；ATM 付款請在 Aircraft Pay 後台退款。',
      refundAmount: '金額（新台幣）',
      refundMax: '最多 {{amount}}。',
      refundConfirm: '退款 {{amount}}',
      refundRequested: '已送出退款。',
      refundStatus: {
        requested: '已申請',
        pending: '處理中',
        succeeded: '已退款',
        failed: '失敗',
      },
    },
    flags: {
      title: '功能旗標',
      description: '對所有工作區開啟，或只對列出的工作區開啟。',
      new: '新增旗標',
      empty: '沒有功能旗標',
      key: '鍵值',
      keyHint: '小寫英文字母、數字、「_」與「.」，須以英文字母開頭。',
      keyInvalid: '請使用小寫英文字母、數字、「_」與「.」，並以英文字母開頭。',
      descriptionLabel: '說明',
      enabled: '開啟',
      workspaces: '只對這些工作區開啟',
      workspacesHint: '工作區 ID，每行一個。留空表示所有工作區。',
      workspacesInvalid: '每一行都必須是工作區 ID（UUID）。',
      state: '狀態',
      updated: '更新時間',
      onAll: '全部開啟',
      onFor_one: '對 {{count}} 個工作區開啟',
      onFor_other: '對 {{count}} 個工作區開啟',
      off: '關閉',
      editTitle: '編輯 {{key}}',
      confirmDelete: '要刪除旗標 {{key}} 嗎？',
    },
    alerts: {
      title: '警示',
      description: '付款路由問題、退款失敗，以及 Smoke 無法對應的付款。',
      showResolved: '顯示已處理',
      empty: '沒有警示',
      workspace: '工作區',
      resolve: '標示為已處理',
      resolved: '已處理',
      field: {
        accountId: '帳號（帳務）',
        orderId: '訂單',
        code: '錯誤代碼',
        status: 'HTTP 狀態',
        paymentId: 'Aircraft Pay 付款',
        refundId: '退款',
        externalRefundId: 'Aircraft Pay 退款',
        failureCode: '失敗代碼',
        assetId: '資產',
      },
      kind: {
        billing_merchant_not_routed: '商家未設定路由（422）',
        billing_refund_failed: '退款失敗',
        billing_payment_unmatched: '無法對應的付款',
        billing_duplicate_purchase: '重複購買',
        billing_charge_rejected: '扣款遭拒',
        github_scan_failed: 'GitHub 掃描失敗',
      },
    },
    feedback: {
      title: '意見回饋',
      description:
        '使用者送來的問題、建議和回饋。等待處理的依送出時間由舊到新排列。儲存回覆或變更狀態時，會寄 Email 通知提出的人。',
      statusFilter: '狀態',
      kindFilter: '類型',
      allKinds: '所有類型',
      filter: {
        open: '等待處理（已收到、已排入計畫、處理中）',
        new: '已收到',
        planned: '已排入計畫',
        in_progress: '處理中',
        done: '已完成',
        declined: '不採納',
        all: '全部',
      },
      empty: '沒有資料',
      replied: '已回覆',
      newCount_one: '{{count}} 則新的',
      newCount_other: '{{count}} 則新的',
      back: '所有意見回饋',
      message: '內容',
      context: '附帶資訊',
      author: '提出者',
      sentAt: '送出時間',
      page: '頁面',
      openWorkspace: '開啟工作區',
      appVersion: 'Smoke 版本',
      browser: '瀏覽器',
      replyTitle: '回覆',
      replyHint: '尚未回覆。',
      repliedBy: '{{email}} 於 {{at}} 回覆。',
      replyLabel: '回覆使用者',
      replyVisible: '使用者會在意見回饋頁和 Email 看到這段文字，不會顯示你的名字。',
      statusLabel: '狀態',
      save: '儲存並通知',
      saved: '已儲存，並已寄 Email 通知使用者。',
      emailNote:
        '儲存新的或修改過的回覆，或變更狀態時，會寄 Email 通知使用者；只移除回覆不會寄信。',
    },
    users: {
      title: '工作人員',
      description: '工作人員角色與工作區角色分開。工作人員必須開啟兩步驟驗證才能使用管理後台。',
      add: '授予工作人員角色',
      role: '角色',
      grant: '授予',
      twoFactorOn: '已開啟兩步驟驗證',
      twoFactorOff: '未開啟兩步驟驗證',
      confirmRemove: '要移除 {{email}} 的工作人員角色嗎？',
    },
  },
};
